import React, { useEffect, useState, useCallback, useMemo } from "react";
import DOMPurify from 'dompurify';
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TiptapLink from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Cropper from 'react-easy-crop';
import { 
  Menu, X, Shield, BookOpen, MapPin, TrendingDown, 
  Facebook, Instagram, Twitter, Calendar, Newspaper, ChevronRight,
  Mail, Phone, MapPinned, ExternalLink, Play, Quote, Award, User,
  Send, CheckCircle, Users, Download, Camera, ChevronLeft, Image, Building2,
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, AlignLeft, AlignCenter, Link2,
  Crop, RotateCw, Settings, FileText, File, Trash2, Eye
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Scroll to top component - scrolls to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Error logging utility - logs errors in development, silent in production
const logError = (context, error) => {
  if (process.env.NODE_ENV === 'development') {
    console.error(`[${context}]`, error);
  }
  // In production, errors could be sent to a monitoring service
};

// Helper to resolve image URLs (handles both uploaded and external images)
const resolveImageUrl = (url) => {
  if (!url) return null;
  
  // If it's already a full URL, return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  
  // Handle /api/uploads/ paths
  if (url.startsWith('/api/uploads') || url.startsWith('/uploads')) {
    // Normalize to /api/uploads format
    const normalizedPath = url.startsWith('/uploads') ? `/api${url}` : url;
    
    // In production (same origin), relative paths work
    // In development/preview, we need to prefix with BACKEND_URL
    if (BACKEND_URL && !BACKEND_URL.includes('localhost')) {
      return `${BACKEND_URL}${normalizedPath}`;
    }
    return normalizedPath;
  }
  
  // Handle local public folder paths (like /merante-council.jpg)
  if (url.startsWith('/')) {
    return url;
  }
  
  return url;
};

// Icon mapping for dynamic icons from database
const ICON_MAP = {
  Shield: Shield,
  BookOpen: BookOpen,
  MapPin: MapPin,
  TrendingDown: TrendingDown,
  Award: Award,
  User: User,
  Users: Users,
  Calendar: Calendar,
  Building2: Building2
};

// Rich Text Editor Component using TipTap
const RichTextEditor = ({ value, onChange }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      TiptapLink.configure({
        openOnClick: false,
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="border border-gray-300 rounded overflow-hidden bg-white">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-1 p-2 bg-gray-50 border-b border-gray-200">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('bold') ? 'bg-gray-200' : ''}`}
          title="Bold"
        >
          <Bold size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('italic') ? 'bg-gray-200' : ''}`}
          title="Italic"
        >
          <Italic size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('underline') ? 'bg-gray-200' : ''}`}
          title="Underline"
        >
          <UnderlineIcon size={18} />
        </button>
        
        <div className="w-px h-6 bg-gray-300 mx-1 self-center" />
        
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={`p-2 rounded hover:bg-gray-200 text-sm font-bold ${editor.isActive('heading', { level: 2 }) ? 'bg-gray-200' : ''}`}
          title="Heading"
        >
          H2
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={`p-2 rounded hover:bg-gray-200 text-sm font-bold ${editor.isActive('heading', { level: 3 }) ? 'bg-gray-200' : ''}`}
          title="Subheading"
        >
          H3
        </button>
        
        <div className="w-px h-6 bg-gray-300 mx-1 self-center" />
        
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('bulletList') ? 'bg-gray-200' : ''}`}
          title="Bullet List"
        >
          <List size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('orderedList') ? 'bg-gray-200' : ''}`}
          title="Numbered List"
        >
          <ListOrdered size={18} />
        </button>
        
        <div className="w-px h-6 bg-gray-300 mx-1 self-center" />
        
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive({ textAlign: 'left' }) ? 'bg-gray-200' : ''}`}
          title="Align Left"
        >
          <AlignLeft size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive({ textAlign: 'center' }) ? 'bg-gray-200' : ''}`}
          title="Align Center"
        >
          <AlignCenter size={18} />
        </button>
        
        <div className="w-px h-6 bg-gray-300 mx-1 self-center" />
        
        <button
          type="button"
          onClick={setLink}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('link') ? 'bg-gray-200' : ''}`}
          title="Add Link"
        >
          <Link2 size={18} />
        </button>
      </div>
      
      {/* Editor */}
      <EditorContent editor={editor} className="prose max-w-none p-4 min-h-[200px] focus:outline-none" />
    </div>
  );
};

// Helper to create cropped image
const createCroppedImage = async (imageSrc, pixelCrop) => {
  const image = new window.Image();
  image.src = imageSrc;
  await new Promise((resolve) => { image.onload = resolve; });

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, 'image/jpeg', 0.9);
  });
};

// Image Cropper Component
const ImageCropperModal = ({ imageSrc, onCropComplete, onCancel, aspectRatio = 1 }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [selectedAspect, setSelectedAspect] = useState(aspectRatio);

  const onCropChange = useCallback((crop) => {
    setCrop(crop);
  }, []);

  const onZoomChange = useCallback((zoom) => {
    setZoom(zoom);
  }, []);

  const onCropCompleteCallback = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleSave = async () => {
    if (croppedAreaPixels) {
      const croppedBlob = await createCroppedImage(imageSrc, croppedAreaPixels);
      onCropComplete(croppedBlob);
    }
  };

  const aspectOptions = [
    { label: 'Square (1:1)', value: 1 },
    { label: 'Landscape (4:3)', value: 4/3 },
    { label: 'Portrait (3:4)', value: 3/4 },
    { label: 'Wide (16:9)', value: 16/9 },
    { label: 'Free', value: null },
  ];

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 bg-black">
        <div className="flex items-center gap-4">
          <Crop className="text-white" size={24} />
          <span className="text-white font-heading text-lg uppercase">Crop Image</span>
        </div>
        <div className="flex items-center gap-4">
          <select
            value={selectedAspect || 'free'}
            onChange={(e) => setSelectedAspect(e.target.value === 'free' ? null : parseFloat(e.target.value))}
            className="bg-gray-800 text-white px-3 py-2 rounded border border-gray-600"
          >
            {aspectOptions.map((opt) => (
              <option key={opt.label} value={opt.value || 'free'}>{opt.label}</option>
            ))}
          </select>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>
      </div>

      {/* Cropper */}
      <div className="flex-1 relative">
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          aspect={selectedAspect}
          onCropChange={onCropChange}
          onCropComplete={onCropCompleteCallback}
          onZoomChange={onZoomChange}
        />
      </div>

      {/* Controls */}
      <div className="bg-black px-6 py-4">
        <div className="flex items-center justify-between max-w-2xl mx-auto">
          <div className="flex items-center gap-4 flex-1">
            <span className="text-white text-sm">Zoom:</span>
            <input
              type="range"
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="flex-1 max-w-xs"
            />
            <span className="text-white text-sm w-12">{Math.round(zoom * 100)}%</span>
          </div>
          <div className="flex gap-4">
            <button
              onClick={onCancel}
              className="px-6 py-2 bg-gray-700 text-white rounded hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-2 bg-campaign-red text-white rounded hover:bg-campaign-red-hover transition-colors font-semibold"
            >
              Apply Crop
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Page title helper
const usePageTitle = (title) => {
  useEffect(() => {
    const baseTitle = "Anthony Merante for New York State Senate - District 35";
    document.title = title ? `${title} | ${baseTitle}` : baseTitle;
  }, [title]);
};

// Campaign data
const CAMPAIGN_DATA = {
  donateUrl: "https://secure.winred.com/merante-for-ny-senate/donate-today",
  facebook: "https://www.facebook.com/Meranteforyonkers",
  instagram: "https://www.instagram.com/merante4yonkers/",
  twitter: null,
  youtube: null,
  logo: "/logo-merante-darkblue.png",
  logoWhite: "/logo-merante-white.png",
  logoDark: "/logo-merante-darkblue.png",
  images: {
    heroMain: "/hero-background.jpg",
    heroCandidate: "/merante-hero-new.png",
    heroMobile: "/merante-hero-new.png",
    council: "/merante-council.jpg",
    voters1: "/merante-voters1.jpg",
    community: "/merante-community.jpg",
    office: "/merante-office.jpg"
  },
  campaignPhotos: [
    { url: "/merante-meeting.jpg", caption: "Engaging with constituents at a community meeting" },
    { url: "/merante-roadcrew.png", caption: "Discussing infrastructure needs with local road workers" },
    { url: "/merante-resident.jpg", caption: "One-on-one conversation with a resident in her home" },
    { url: "/merante-lemonade.jpg", caption: "Supporting young entrepreneurs at a neighborhood lemonade stand" },
    { url: "/merante-townhall.jpg", caption: "Speaking at a town hall meeting about community issues" },
    { url: "/merante-kitchen.jpg", caption: "Listening to residents' concerns at their kitchen table" },
    { url: "/merante-veteran.png", caption: "Speaking with a veteran about local issues" },
    { url: "/merante-parade.jpg", caption: "Marching in the Puerto Rican Day Parade" },
    { url: "/merante-irish-parade.jpg", caption: "With the Irish Consulate at the St. Patrick's Day Parade" },
    { url: "/merante-speaking.jpg", caption: "Speaking at a charter school community event" },
    { url: "/merante-pledge.jpg", caption: "Standing for the Pledge of Allegiance at a civic ceremony" },
    { url: "/merante-rotary.jpg", caption: "Speaking at the Rotary Club of Yonkers-East Yonkers" },
    { url: "/merante-donation.jpg", caption: "Presenting a donation to Beach Hill Residents Association" },
    { url: "/merante-library.jpg", caption: "Meeting with families at a community library event" },
    { url: "/merante-cubscout.jpg", caption: "Meeting with a Cub Scout and family at a community event" },
    { url: "/merante-italian.jpg", caption: "Connecting with supporters at an Italian-American club event" },
    { url: "/merante-supporters.jpg", caption: "Anthony with campaign supporters in the community" },
    { url: "/merante-stpatricks.jpg", caption: "Celebrating St. Patrick's Day with supporters" },
    { url: "/merante-supporter.jpg", caption: "Meeting with a supporter at a campaign event" },
    { url: "/merante-voters1.jpg", caption: "Speaking with voters in the community" },
    { url: "/merante-community.jpg", caption: "Meeting with residents at Runyon Heights Community House" },
    { url: "/merante-council.jpg", caption: "Anthony at Yonkers City Council" },
  ],
  gallery: [],
  tagline: "A Proven Fighter for Taxpayers",
  slogan: "Merante for New York State Senate",
  subSlogan: "Standing Up for Taxpayers, Homeowners, and Working Families",
  endSlogan: "Your Voice Deserves to Be Heard in Albany.",
  bio: `Anthony Merante is a lifelong Westchester County resident, successful small business owner, and former Yonkers City Councilmember dedicated to fighting for taxpayers and strengthening local communities. A Certified Public Accountant with decades of experience in business and finance, Anthony built his career helping families and small businesses succeed while creating jobs and investing in the local economy.`,
  bioShort: `During his time on the Yonkers City Council, Anthony earned a reputation as a strong voice for fiscal responsibility, government accountability, and common-sense leadership. He consistently stood up for taxpayers, worked to control spending, and fought to improve neighborhood services and infrastructure across the city.`,
  bioExtended: `Anthony's commitment to public service began long before holding office through years of community involvement and leadership in local civic organizations. He understands the challenges facing working families because he lives them every day as a husband, father, and neighbor. Now running for New York State Senate, Anthony Merante is focused on bringing honest leadership, practical solutions, and a strong voice for the people of Westchester and the Hudson Valley.`,
  contact: {
    website: "MeranteForSenate.com",
    email: "info@MeranteForSenate.com",
    phone: "914-546-7040",
    address: "46 Davenport Road, Yonkers, NY 10710",
    paidFor: "Merante for NY Senate"
  },
  donateAmounts: [25, 50, 100, 250, 500, 1000],
  youtubeVideo: null,
  youtubeThumb: null,
  upcomingEvent: null,
  issues: [
    { 
      title: "Skyrocketing Energy Bills", 
      iconImage: "/icon-energy.png", 
      desc: "Democrats keep rubber stamping Con Edison rate increases while families struggle to pay their bills. The Governor's Public Service Commission is stacked with energy industry insiders who approve rate hikes while New Yorkers pay the price.",
      details: [
        "Stop rubber-stamping rate increases",
        "Reform the Public Service Commission",
        "Protect families from utility abuse",
        "Lower energy costs for all"
      ]
    },
    { 
      title: "High Taxes", 
      iconImage: "/icon-taxes.png", 
      desc: "New York is forcing people out. We have some of the highest taxes in the nation. Working families are leaving. Seniors on fixed incomes are being driven out of the communities they spent their lives building.",
      details: [
        "Control Albany spending",
        "Stop driving families out",
        "Protect seniors on fixed incomes",
        "Keep grandparents near families"
      ]
    },
    { 
      title: "Catch and Release", 
      iconImage: "/icon-bail.png", 
      desc: "So called bail reform has turned our justice system into a revolving door. Criminals are arrested and released right back onto our streets. Families and small businesses are paying the price.",
      details: [
        "Reform bail laws",
        "Keep dangerous criminals off streets",
        "Protect families and businesses",
        "Restore public safety"
      ]
    },
    { 
      title: "Immigration Costs", 
      iconImage: "/icon-immigration.png", 
      desc: "Albany is spending $4.3 billion of your tax dollars to house illegal migrants in hotels across the state while hardworking New Yorkers struggle to pay rent and property taxes.",
      details: [
        "Stop hotel housing for migrants",
        "Fund public schools properly",
        "Prioritize hardworking New Yorkers",
        "Responsible immigration policy"
      ]
    },
    { 
      title: "Local Zoning Takeover", 
      iconImage: "/icon-zoning.png", 
      desc: "Albany wants to override local zoning and build massive housing projects that would overcrowd and destroy neighborhoods. We must stop this Albany power grab.",
      details: [
        "Stop Albany power grab",
        "Protect local zoning decisions",
        "Prevent neighborhood destruction",
        "Keep control local"
      ]
    },
  ]
};

// Navbar Component
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav data-testid="navbar" className={`${scrolled ? 'fixed top-0 left-0 right-0 shadow-lg' : 'relative'} z-50 transition-all duration-300 bg-[#000033]/90 backdrop-blur-sm`}>
      <div className="container-campaign">
        <div className="flex items-center justify-between h-20">
          {/* Logo - hidden at top, visible when scrolled */}
          <Link to="/" className={`flex items-center transition-opacity duration-300 ${scrolled ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} data-testid="nav-logo">
            <img 
              src={CAMPAIGN_DATA.logoWhite} 
              alt="Merante for State Senate" 
              className="h-14 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-10">
            <Link to="/" className="text-white hover:text-gray-300 text-lg font-black transition-colors duration-200" data-testid="nav-home">Home</Link>
            <Link to="/about" className="text-white hover:text-gray-300 text-lg font-black transition-colors duration-200" data-testid="nav-about">About</Link>
            <Link to="/events" className="text-white hover:text-gray-300 text-lg font-black transition-colors duration-200" data-testid="nav-events">Events</Link>
            <Link to="/news" className="text-white hover:text-gray-300 text-lg font-black transition-colors duration-200" data-testid="nav-news">News</Link>
            <Link to="/contact" className="text-white hover:text-gray-300 text-lg font-black transition-colors duration-200" data-testid="nav-contact">Contact</Link>
            <a 
              href={CAMPAIGN_DATA.donateUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-[#3366cc] hover:bg-[#2855b3] text-white font-bold uppercase tracking-wider py-3 px-6 transition-all duration-300"
              data-testid="nav-donate-btn"
            >
              Donate
            </a>
          </div>

          {/* Mobile menu button */}
          <button 
            onClick={() => setIsOpen(!isOpen)} 
            className="md:hidden p-2 text-white"
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden mobile-menu-enter pb-4" data-testid="mobile-menu">
            <div className="flex flex-col space-y-4">
              <Link to="/" className="text-white hover:text-gray-300 py-2 font-black" onClick={() => setIsOpen(false)}>Home</Link>
              <Link to="/about" className="text-white hover:text-gray-300 py-2 font-black" onClick={() => setIsOpen(false)}>About</Link>
              <Link to="/events" className="text-white hover:text-gray-300 py-2 font-black" onClick={() => setIsOpen(false)}>Events</Link>
              <Link to="/news" className="text-white hover:text-gray-300 py-2 font-black" onClick={() => setIsOpen(false)}>News</Link>
              <Link to="/contact" className="text-white hover:text-gray-300 py-2 font-black" onClick={() => setIsOpen(false)}>Contact</Link>
              <a 
                href={CAMPAIGN_DATA.donateUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-[#3366cc] hover:bg-[#2855b3] text-white font-bold uppercase tracking-wider py-3 px-6 text-center"
              >
                Donate
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-[#000033] text-white py-16">
      <div className="container-campaign">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Logo & Tagline */}
          <div className="col-span-1 md:col-span-2">
            <img 
              src={CAMPAIGN_DATA.logoWhite} 
              alt="Merante for State Senate" 
              className="h-20 w-auto mb-6"
            />
            <p className="text-gray-300 mb-2 font-black">
              {CAMPAIGN_DATA.slogan}
            </p>
            <p className="text-gray-400 mb-4 font-body">
              {CAMPAIGN_DATA.tagline}
            </p>
            <div className="flex space-x-4">
              {CAMPAIGN_DATA.facebook && (
                <a href={CAMPAIGN_DATA.facebook} target="_blank" rel="noopener noreferrer" className="social-icon" data-testid="footer-facebook">
                  <Facebook size={20} />
                </a>
              )}
              {CAMPAIGN_DATA.instagram && (
                <a href={CAMPAIGN_DATA.instagram} target="_blank" rel="noopener noreferrer" className="social-icon" data-testid="footer-instagram">
                  <Instagram size={20} />
                </a>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-xl uppercase mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li><Link to="/" className="text-gray-300 hover:text-white transition-colors font-semibold">Home</Link></li>
              <li><Link to="/about" className="text-gray-300 hover:text-white transition-colors font-semibold">About Anthony</Link></li>
              <li><Link to="/events" className="text-gray-300 hover:text-white transition-colors font-semibold">Events</Link></li>
              <li><Link to="/news" className="text-gray-300 hover:text-white transition-colors font-semibold">In The News</Link></li>
              <li><Link to="/contact" className="text-gray-300 hover:text-white transition-colors font-semibold">Contact Us</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading text-xl uppercase mb-4">Contact</h4>
            <ul className="space-y-3 text-gray-300">
              <li className="flex items-center gap-2">
                <Phone size={16} className="text-[#3366cc]" />
                <a href={`tel:${CAMPAIGN_DATA.contact.phone}`} className="hover:text-white transition-colors">
                  {CAMPAIGN_DATA.contact.phone}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Mail size={16} className="text-[#3366cc]" />
                <a href={`mailto:${CAMPAIGN_DATA.contact.email}`} className="hover:text-white transition-colors">
                  {CAMPAIGN_DATA.contact.email}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPinned size={16} className="text-[#3366cc] mt-1 flex-shrink-0" />
                <span>
                  46 Davenport Road<br />
                  Yonkers, NY 10710
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/20 mt-12 pt-8 text-center text-gray-400 text-sm">
          <p className="font-semibold">Paid for by {CAMPAIGN_DATA.contact.paidFor}</p>
          <p className="mt-2">&copy; 2025 Anthony Merante</p>
        </div>
      </div>
    </footer>
  );
};

// Hero Section with candidate photo overlay
const HeroSection = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [customCta, setCustomCta] = useState({ enabled: false, text: '', url: '' });

  useEffect(() => {
    const fetchCustomCta = async () => {
      try {
        const res = await axios.get(`${API}/custom-cta`);
        setCustomCta(res.data);
      } catch (err) {
        logError('fetchCustomCta', err);
      }
    };
    fetchCustomCta();
  }, []);

  const handleQuickSignup = async (e) => {
    e.preventDefault();
    if (!email) return;
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/subscribe`, { email, wants_email: true });
      setSubmitStatus('success');
      setEmail('');
      setTimeout(() => setSubmitStatus(null), 3000);
    } catch (err) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(null), 3000);
    }
    setIsSubmitting(false);
  };

  return (
    <>
      {/* Mobile Hero - Photo at top aligned to bottom, CTAs below with white background */}
      <section data-testid="hero-section-mobile" className="lg:hidden">
        {/* Mobile: Candidate Photo with overlay background */}
        <div className="relative h-[520px] overflow-hidden">
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${CAMPAIGN_DATA.images.heroMain})` }}
          />
          <div className="absolute inset-0 bg-[#000033]/50" />
          <div className="relative z-10 h-full flex items-end justify-center">
            <img 
              src={CAMPAIGN_DATA.images.heroCandidate}
              alt="Anthony Merante"
              className="h-[500px] w-auto object-contain object-bottom"
              data-testid="hero-candidate-image-mobile"
            />
          </div>
        </div>
        
        {/* Mobile: CTAs with white background */}
        <div className="bg-white py-8 px-4">
          <div className="text-center">
            <img 
              src={CAMPAIGN_DATA.logoDark}
              alt="Anthony Merante for State Senate"
              className="h-16 w-auto mx-auto mb-4"
            />
            <p className="text-base text-gray-700 font-body mb-6">
              {CAMPAIGN_DATA.tagline}
            </p>
            
            {/* Quick Email Signup */}
            <form onSubmit={handleQuickSignup} className="mb-6">
              <div className="flex flex-col gap-3 max-w-sm mx-auto">
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded text-[#000033] font-body"
                  data-testid="hero-email-input-mobile"
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#3366cc] hover:bg-[#2855b3] text-white px-6 py-3 font-heading uppercase tracking-wide transition-colors disabled:opacity-50"
                  data-testid="hero-signup-btn-mobile"
                >
                  {isSubmitting ? 'Signing up...' : 'Stay Updated'}
                </button>
              </div>
              {submitStatus === 'success' && (
                <p className="text-green-600 mt-2 font-body">Thanks for signing up!</p>
              )}
              {submitStatus === 'error' && (
                <p className="text-red-600 mt-2 font-body">Something went wrong. Please try again.</p>
              )}
            </form>
            
            {/* CTA Buttons */}
            <div className="flex flex-col gap-3 max-w-sm mx-auto">
              <a 
                href={CAMPAIGN_DATA.donateUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="block bg-[#c41920] text-white px-8 py-4 text-lg font-heading uppercase tracking-wide hover:bg-[#a31518] transition-colors text-center"
                data-testid="hero-donate-btn-mobile"
              >
                Donate Now
              </a>
              <Link 
                to="/signup"
                className="block border-2 border-[#000033] text-[#000033] px-8 py-4 text-lg font-heading uppercase tracking-wide hover:bg-[#000033] hover:text-white transition-colors text-center"
                data-testid="hero-signup-page-btn-mobile"
              >
                Get Involved
              </Link>
              {customCta.enabled && customCta.text && customCta.url && (
                <a 
                  href={customCta.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block bg-[#3366cc] text-white px-8 py-4 text-lg font-heading uppercase tracking-wide hover:bg-[#2855b3] transition-colors text-center"
                  data-testid="hero-custom-cta-btn-mobile"
                >
                  {customCta.text}
                </a>
              )}
            </div>
          </div>
        </div>
      </section>
      
      {/* Desktop Hero - Full width with photo aligned to bottom */}
      <section data-testid="hero-section" className="hidden lg:block relative h-[680px] overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${CAMPAIGN_DATA.images.heroMain})` }}
        />
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-[#000033]/70" />
        
        {/* Content Container */}
        <div className="relative z-10 h-full">
          <div className="container-campaign h-full">
            <div className="flex h-full items-center">
              {/* Left: Text Content */}
              <div className="w-1/2 pr-8">
                {/* Campaign Logo instead of text */}
                <img 
                  src={CAMPAIGN_DATA.logoWhite}
                  alt="Anthony Merante for State Senate"
                  className="w-[400px] h-auto mb-6"
                  data-testid="hero-logo"
                />
                
                {/* Subheadline */}
                <p className="text-xl xl:text-2xl text-white/90 font-body mb-6">
                  {CAMPAIGN_DATA.tagline}
                </p>
                
                {/* Quick Email Signup */}
                <form onSubmit={handleQuickSignup} className="mb-6">
                  <div className="flex gap-3">
                    <input
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="flex-1 px-4 py-3 rounded text-[#000033] font-body"
                      data-testid="hero-email-input"
                    />
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="bg-[#3366cc] hover:bg-[#2855b3] text-white px-6 py-3 font-heading uppercase tracking-wide transition-colors disabled:opacity-50"
                      data-testid="hero-signup-btn"
                    >
                      {isSubmitting ? 'Signing up...' : 'Stay Updated'}
                    </button>
                  </div>
                  {submitStatus === 'success' && (
                    <p className="text-green-400 mt-2 font-body">Thanks for signing up!</p>
                  )}
                  {submitStatus === 'error' && (
                    <p className="text-red-400 mt-2 font-body">Something went wrong. Please try again.</p>
                  )}
                </form>
                
                {/* CTA Buttons */}
                <div className="flex gap-4 flex-wrap">
                  <a 
                    href={CAMPAIGN_DATA.donateUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-block bg-[#c41920] text-white px-8 py-4 text-lg font-heading uppercase tracking-wide hover:bg-[#a31518] transition-colors"
                    data-testid="hero-donate-btn"
                  >
                    Donate Now
                  </a>
                  <Link 
                    to="/signup"
                    className="inline-block border-2 border-white text-white px-8 py-4 text-lg font-heading uppercase tracking-wide hover:bg-white hover:text-[#000033] transition-colors"
                    data-testid="hero-signup-page-btn"
                  >
                    Get Involved
                  </Link>
                  {customCta.enabled && customCta.text && customCta.url && (
                    <a 
                      href={customCta.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block bg-[#3366cc] text-white px-8 py-4 text-lg font-heading uppercase tracking-wide hover:bg-[#2855b3] transition-colors"
                      data-testid="hero-custom-cta-btn"
                    >
                      {customCta.text}
                    </a>
                  )}
                </div>
              </div>
              
              {/* Right: Candidate Photo - aligned to bottom */}
              <div className="w-1/2 h-full flex items-end justify-center">
                <img 
                  src={CAMPAIGN_DATA.images.heroCandidate}
                  alt="Anthony Merante"
                  className="h-[650px] w-auto object-contain object-bottom drop-shadow-2xl"
                  data-testid="hero-candidate-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

// Meet Anthony Section
const MeetAnthonySection = () => {
  return (
    <section data-testid="meet-section" className="py-20 bg-white">
      <div className="container-campaign">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <p className="text-[#3366cc] font-black uppercase tracking-wider mb-2">{CAMPAIGN_DATA.tagline}</p>
            <h2 className="font-heading text-4xl md:text-5xl text-[#000033] uppercase mb-6" data-testid="meet-title">
              Meet Anthony Merante
            </h2>
            <p className="text-lg text-gray-700 font-body leading-relaxed mb-6">
              {CAMPAIGN_DATA.bio}
            </p>
            <p className="text-gray-600 font-body leading-relaxed mb-6">
              {CAMPAIGN_DATA.bioShort}
            </p>
            <p className="text-gray-600 font-body leading-relaxed mb-8">
              {CAMPAIGN_DATA.bioExtended}
            </p>
            <Link to="/about" className="inline-flex items-center gap-2 bg-transparent border-2 border-[#000033] text-[#000033] hover:bg-[#000033] hover:text-white font-bold uppercase tracking-wider py-4 px-8 transition-all duration-300">
              Read Full Bio <ChevronRight size={20} />
            </Link>
          </div>
          <div className="order-1 lg:order-2">
            <img 
              src="/merante-council.jpg"
              alt="Anthony Merante at Yonkers City Council" 
              className="w-full h-auto rounded-sm shadow-2xl"
              data-testid="meet-image"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// Issues Section
const IssuesSection = ({ issues }) => {
  // Use provided issues from API or fall back to static data
  const displayIssues = issues && issues.length > 0 ? issues : CAMPAIGN_DATA.issues;
  
  return (
    <section data-testid="issues-section" className="py-20 bg-gray-50">
      <div className="container-campaign">
        <div className="text-center mb-12">
          <h2 className="font-heading text-4xl md:text-5xl text-[#000033] uppercase mb-4" data-testid="issues-title">
            Fighting for You on the Issues
          </h2>
          <p className="text-lg text-gray-600 font-body max-w-2xl mx-auto">
            {CAMPAIGN_DATA.subSlogan}
          </p>
        </div>
        
        <div className="flex flex-wrap justify-center gap-8">
          {displayIssues.map((issue, index) => (
            <div 
              key={issue.id || index} 
              className="bg-white p-8 rounded-sm shadow-sm card-hover w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.5rem)]"
              data-testid={`issue-card-${index}`}
            >
              <div className="w-16 h-16 rounded-full bg-[#3366cc]/10 flex items-center justify-center mb-4">
                {issue.iconImage ? (
                  <img src={issue.iconImage} alt={issue.title} className="w-10 h-10 object-contain" />
                ) : (
                  (() => {
                    const IconComponent = typeof issue.icon === 'string' ? (ICON_MAP[issue.icon] || Shield) : (issue.icon || Shield);
                    return <IconComponent className="w-8 h-8 text-[#3366cc]" />;
                  })()
                )}
              </div>
              <h3 className="font-heading text-xl uppercase text-[#000033] mb-3">{issue.title}</h3>
              <p className="text-gray-600 font-body text-sm">{issue.description || issue.desc}</p>
            </div>
          ))}
        </div>
        
        <div className="text-center mt-12">
          <p className="text-2xl font-heading uppercase text-[#c41920]">{CAMPAIGN_DATA.endSlogan}</p>
          <a 
            href={CAMPAIGN_DATA.donateUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-block mt-6 bg-[#3366cc] text-white px-10 py-4 text-lg font-heading uppercase tracking-wide hover:bg-[#2855b3] transition-colors"
          >
            Support the Campaign
          </a>
        </div>
      </div>
    </section>
  );
};

// Video Section - Commented out for now (no video available)
// const VideoSection = () => {
//   if (!CAMPAIGN_DATA.youtube) return null;
//   return (
//     <section data-testid="video-section" className="py-20 bg-black">
//       <div className="container-campaign">
//         <div className="text-center mb-12">
//           <h2 className="font-heading text-4xl md:text-5xl text-white uppercase mb-4" data-testid="video-title">
//             Campaign Announcement
//           </h2>
//         </div>
//         <div className="max-w-4xl mx-auto">
//           <div className="video-container rounded-sm overflow-hidden shadow-2xl">
//             <iframe 
//               src={CAMPAIGN_DATA.youtube}
//               title="Brandon Neider Campaign"
//               frameBorder="0"
//               allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
//               allowFullScreen
//               data-testid="youtube-video"
//             />
//           </div>
//         </div>
//       </div>
//     </section>
//   );
// };

// Photo Gallery Section
const GallerySection = ({ photos }) => {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [showSlideshow, setShowSlideshow] = useState(false);
  
  // Use provided photos or fall back to static gallery
  const galleryPhotos = photos && photos.length > 0 ? photos : CAMPAIGN_DATA.gallery;
  
  // Show only first 18 photos (6 rows x 3 columns) in the grid
  const maxVisible = 18;
  const visiblePhotos = galleryPhotos.slice(0, maxVisible);
  const hasMore = galleryPhotos.length > maxVisible;
  
  const openSlideshow = (index) => {
    setSelectedIndex(index);
    setShowSlideshow(true);
  };
  
  const closeSlideshow = () => {
    setShowSlideshow(false);
    setSelectedIndex(null);
  };
  
  const goToPrev = (e) => {
    e.stopPropagation();
    setSelectedIndex((prev) => (prev === 0 ? galleryPhotos.length - 1 : prev - 1));
  };
  
  const goToNext = (e) => {
    e.stopPropagation();
    setSelectedIndex((prev) => (prev === galleryPhotos.length - 1 ? 0 : prev + 1));
  };
  
  // Handle keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showSlideshow) return;
      if (e.key === 'ArrowLeft') goToPrev(e);
      if (e.key === 'ArrowRight') goToNext(e);
      if (e.key === 'Escape') closeSlideshow();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showSlideshow]);

  return (
    <section data-testid="gallery-section" className="py-20 bg-gray-50">
      <div className="container-campaign">
        <div className="text-center mb-12">
          <h2 className="font-heading text-4xl md:text-5xl text-[#000033] uppercase mb-4" data-testid="gallery-title">
            On The Campaign Trail
          </h2>
          <p className="text-lg text-gray-600 font-body">
            Anthony Merante meeting with voters and community leaders across District 35
          </p>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {visiblePhotos.map((photo, index) => (
            <div 
              key={photo.id || index}
              className="relative aspect-square overflow-hidden rounded-sm cursor-pointer group bg-gray-200"
              onClick={() => openSlideshow(index)}
              data-testid={`gallery-image-${index}`}
            >
              <img 
                src={resolveImageUrl(photo.url)} 
                alt={photo.caption || 'Campaign photo'}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.classList.add('flex', 'items-center', 'justify-center', 'bg-gray-100');
                  // Create fallback element safely without innerHTML
                  const fallback = document.createElement('div');
                  fallback.className = 'text-gray-400 text-center p-4';
                  fallback.textContent = 'Image unavailable';
                  e.target.parentElement.appendChild(fallback);
                }}
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center">
                <Camera className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            </div>
          ))}
        </div>
        
        {/* See More Button */}
        {hasMore && (
          <div className="text-center mt-8">
            <button
              onClick={() => openSlideshow(0)}
              className="bg-[#000033] text-white px-8 py-3 font-heading uppercase tracking-wide hover:bg-[#000044] transition-colors inline-flex items-center gap-2"
              data-testid="gallery-see-more"
            >
              <Camera size={20} />
              See All {galleryPhotos.length} Photos
            </button>
          </div>
        )}
      </div>
      
      {/* Full-screen Slideshow */}
      {showSlideshow && selectedIndex !== null && (
        <div 
          className="fixed inset-0 bg-black z-50 flex flex-col"
          onClick={closeSlideshow}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 bg-black/50">
            <span className="text-white font-heading">
              {selectedIndex + 1} / {galleryPhotos.length}
            </span>
            <button 
              className="text-white hover:text-gray-300 transition-colors"
              onClick={closeSlideshow}
              data-testid="slideshow-close"
            >
              <X size={32} />
            </button>
          </div>
          
          {/* Main Content */}
          <div className="flex-1 flex items-center justify-center relative px-4" onClick={(e) => e.stopPropagation()}>
            {/* Previous Button */}
            <button
              onClick={goToPrev}
              className="absolute left-4 md:left-8 z-10 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-colors"
              data-testid="slideshow-prev"
            >
              <ChevronLeft size={32} />
            </button>
            
            {/* Image */}
            <div className="max-w-5xl max-h-[75vh] flex items-center justify-center">
              <img 
                src={resolveImageUrl(galleryPhotos[selectedIndex].url)} 
                alt={galleryPhotos[selectedIndex].caption || 'Campaign photo'}
                className="max-w-full max-h-[75vh] object-contain"
              />
            </div>
            
            {/* Next Button */}
            <button
              onClick={goToNext}
              className="absolute right-4 md:right-8 z-10 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-colors"
              data-testid="slideshow-next"
            >
              <ChevronRight size={32} />
            </button>
          </div>
          
          {/* Caption */}
          <div className="px-6 py-4 bg-black/50 text-center">
            <p className="text-white font-body">
              {galleryPhotos[selectedIndex].caption || 'On the campaign trail'}
            </p>
          </div>
          
          {/* Thumbnail Strip */}
          <div className="bg-black/80 px-4 py-3 overflow-x-auto">
            <div className="flex gap-2 justify-center min-w-max">
              {galleryPhotos.map((photo, index) => (
                <button
                  key={photo.id || index}
                  onClick={(e) => { e.stopPropagation(); setSelectedIndex(index); }}
                  className={`w-16 h-16 rounded overflow-hidden flex-shrink-0 transition-all ${
                    index === selectedIndex ? 'ring-2 ring-white scale-110' : 'opacity-50 hover:opacity-100'
                  }`}
                >
                  <img 
                    src={resolveImageUrl(photo.url)} 
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

// Events Section
const EventsSection = ({ events, showAll = false }) => {
  const displayEvents = showAll ? events : events.slice(0, 3);
  
  return (
    <section data-testid="events-section" className="py-20 bg-white">
      <div className="container-campaign">
        <div className="text-center mb-12">
          <h2 className="font-heading text-4xl md:text-5xl text-[#000033] uppercase mb-4" data-testid="events-title">
            Upcoming Events
          </h2>
          <p className="text-lg text-gray-600 font-body">
            Join us on the campaign trail
          </p>
        </div>
        
        {displayEvents.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-sm">
            <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 font-body">No upcoming events at this time. Check back soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {displayEvents.map((event, index) => (
              <div 
                key={event.id || index} 
                className="bg-gray-50 rounded-sm overflow-hidden card-hover flex flex-col"
                data-testid={`event-card-${index}`}
              >
                {/* Event Thumbnail - shown if image_url exists */}
                {event.image_url && (
                  <div className="w-full aspect-[16/9] bg-gray-200 overflow-hidden">
                    <img 
                      src={resolveImageUrl(event.image_url)} 
                      alt={event.title}
                      className="w-full h-full object-contain bg-white"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center gap-2 text-[#3366cc] font-semibold mb-2">
                    <Calendar size={18} />
                    <span>{event.date}</span>
                    {event.time && <span>| {event.time}</span>}
                  </div>
                  <h3 className="font-heading text-xl text-[#000033] uppercase mb-2">{event.title}</h3>
                  <p className="text-gray-600 font-body mb-4 flex-1">{event.description}</p>
                  {event.location && (
                    <p className="text-gray-500 text-sm mb-4 flex items-center gap-1">
                      <MapPin size={14} /> {event.location}
                    </p>
                  )}
                  <a 
                    href={event.rsvp_link || "#"} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-block bg-[#000033] text-white font-bold uppercase tracking-wider py-2 px-6 text-sm hover:bg-[#000044] transition-colors self-start"
                  >
                    RSVP
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {!showAll && events.length > 3 && (
          <div className="text-center mt-8">
            <Link to="/events" className="inline-flex items-center gap-2 bg-transparent border-2 border-[#000033] text-[#000033] hover:bg-[#000033] hover:text-white font-bold uppercase tracking-wider py-4 px-8 transition-all duration-300">
              View All Events <ChevronRight size={20} />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

// News Section
const NewsSection = ({ news, showAll = false }) => {
  const [selectedArticle, setSelectedArticle] = useState(null);
  const displayNews = showAll ? news : news.slice(0, 3);
  
  // Extract YouTube video ID
  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return match ? match[1] : null;
  };
  
  return (
    <section data-testid="news-section" className="py-20 bg-gray-50">
      <div className="container-campaign">
        <div className="text-center mb-12">
          <h2 className="font-heading text-4xl md:text-5xl text-[#000033] uppercase mb-4" data-testid="news-title">
            In The News
          </h2>
          <p className="text-lg text-gray-600 font-body">
            Latest coverage and updates from the campaign
          </p>
        </div>
        
        {displayNews.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-sm">
            <Newspaper className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 font-body">No news articles yet. Check back soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {displayNews.map((article, index) => {
              const isOriginal = article.content_type === 'original';
              const videoId = getYouTubeId(article.video_url);
              
              return (
                <div 
                  key={article.id || index} 
                  className="bg-white rounded-sm overflow-hidden shadow-sm card-hover"
                  data-testid={`news-card-${index}`}
                >
                  {/* Video thumbnail or image */}
                  {videoId ? (
                    <div 
                      className="relative w-full h-48 bg-black cursor-pointer group"
                      onClick={() => setSelectedArticle(article)}
                    >
                      <img 
                        src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`} 
                        alt={article.title} 
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/50 transition-colors">
                        <div className="w-16 h-16 bg-campaign-red rounded-full flex items-center justify-center">
                          <Play className="w-8 h-8 text-white ml-1" />
                        </div>
                      </div>
                      {isOriginal && (
                        <span className="absolute top-2 left-2 bg-campaign-red text-white text-xs px-2 py-1 font-semibold uppercase">
                          Video
                        </span>
                      )}
                    </div>
                  ) : article.image_url ? (
                    <div className="relative">
                      <img src={resolveImageUrl(article.image_url)} alt={article.title} className="w-full h-48 object-cover" />
                      {isOriginal && (
                        <span className="absolute top-2 left-2 bg-campaign-red text-white text-xs px-2 py-1 font-semibold uppercase">
                          Campaign Update
                        </span>
                      )}
                    </div>
                  ) : isOriginal && (
                    <div className="w-full h-48 bg-black flex items-center justify-center">
                      <span className="text-white font-heading text-2xl uppercase">Campaign Update</span>
                    </div>
                  )}
                  
                  <div className="p-6">
                    <div className="text-sm text-gray-500 mb-2">
                      {article.published_date} {!isOriginal && article.source && `| ${article.source}`}
                    </div>
                    <h3 className="font-heading text-xl text-black uppercase mb-2">{article.title}</h3>
                    <p className="text-gray-600 font-body mb-4">{article.summary}</p>
                    
                    {isOriginal ? (
                      <button 
                        onClick={() => setSelectedArticle(article)}
                        className="text-campaign-red font-semibold hover:underline inline-flex items-center gap-1"
                      >
                        {videoId ? 'Watch Video' : 'Read More'} <ChevronRight size={14} />
                      </button>
                    ) : article.link && (
                      <a 
                        href={article.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-campaign-red font-semibold hover:underline inline-flex items-center gap-1"
                      >
                        Read More <ExternalLink size={14} />
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {!showAll && news.length > 3 && (
          <div className="text-center mt-8">
            <Link to="/news" className="btn-secondary inline-flex items-center gap-2">
              View All News <ChevronRight size={20} />
            </Link>
          </div>
        )}
      </div>
      
      {/* Article Modal for Original Content */}
      {selectedArticle && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => setSelectedArticle(null)}
        >
          <div 
            className="bg-white rounded-sm max-w-4xl w-full max-h-[90vh] overflow-y-auto my-8"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <span className="text-sm text-gray-500">{selectedArticle.published_date}</span>
              <button 
                onClick={() => setSelectedArticle(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>
            
            {/* Video embed */}
            {getYouTubeId(selectedArticle.video_url) && (
              <div className="aspect-video w-full bg-black">
                <iframe
                  src={`https://www.youtube.com/embed/${getYouTubeId(selectedArticle.video_url)}?autoplay=1`}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={selectedArticle.title}
                />
              </div>
            )}
            
            {/* Content */}
            <div className="p-6 md:p-10">
              <h2 className="font-heading text-3xl md:text-4xl text-black uppercase mb-4">
                {selectedArticle.title}
              </h2>
              
              {selectedArticle.image_url && !getYouTubeId(selectedArticle.video_url) && (
                <img 
                  src={resolveImageUrl(selectedArticle.image_url)} 
                  alt={selectedArticle.title}
                  className="w-full max-h-96 object-cover rounded mb-6"
                />
              )}
              
              <div className="prose prose-lg max-w-none">
                {selectedArticle.content ? (
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedArticle.content) }} />
                ) : (
                  <p className="text-gray-600 font-body text-lg">{selectedArticle.summary}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

// Quick Donate Section (appears early on homepage)
// Smart Face Image - positions image based on detected face position
const SmartFaceImage = ({ src, alt, className, facePosition, style = {} }) => {
  const objectPosition = facePosition 
    ? `${facePosition.x}% ${facePosition.y}%` 
    : '50% 50%';
  
  return (
    <img 
      src={src}
      alt={alt}
      className={className}
      style={{ 
        ...style, 
        objectFit: 'cover',
        objectPosition 
      }}
    />
  );
};

// Video Section with Modal Popup
const VideoSection = () => {
  const [isOpen, setIsOpen] = useState(false);
  const videoId = 'XczK87ZDflc';
  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  
  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden'; // Prevent background scroll
    }
    return () => {
      window.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  return (
    <>
      {/* Video Preview Section */}
      <section data-testid="video-section" className="py-16 bg-white">
        <div className="container-campaign">
          <div className="text-center mb-8">
            <h2 className="font-heading text-3xl md:text-4xl text-[#000033] uppercase mb-3">
              Watch Anthony's Message
            </h2>
            <p className="text-lg text-gray-600 font-body">
              Hear directly from Anthony about his vision for District 35
            </p>
          </div>
          
          {/* Video Thumbnail with Play Button */}
          <div 
            className="relative max-w-3xl mx-auto cursor-pointer group"
            onClick={() => setIsOpen(true)}
            data-testid="video-thumbnail"
          >
            <div className="relative aspect-video rounded-lg overflow-hidden shadow-xl">
              <img 
                src={thumbnailUrl}
                alt="Watch Anthony's Message"
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
              {/* Dark overlay on hover */}
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition-colors duration-300" />
              
              {/* Play Button */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-20 h-20 md:w-24 md:h-24 bg-[#c41920] rounded-full flex items-center justify-center shadow-lg transform transition-transform duration-300 group-hover:scale-110">
                  <Play className="w-10 h-10 md:w-12 md:h-12 text-white ml-1" fill="white" />
                </div>
              </div>
              
              {/* Watch Now Text */}
              <div className="absolute bottom-4 left-0 right-0 text-center">
                <span className="bg-[#000033]/80 text-white px-4 py-2 rounded font-heading uppercase tracking-wide text-sm">
                  Click to Watch
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Modal Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => setIsOpen(false)}
          data-testid="video-modal"
        >
          {/* Dark backdrop */}
          <div className="absolute inset-0 bg-black/90" />
          
          {/* Modal content */}
          <div 
            className="relative w-full max-w-5xl z-10"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setIsOpen(false)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors flex items-center gap-2"
              data-testid="video-close-btn"
            >
              <span className="font-heading uppercase tracking-wide">Close</span>
              <X size={28} />
            </button>
            
            {/* Video iframe */}
            <div className="aspect-video bg-black rounded-lg overflow-hidden shadow-2xl">
              <iframe
                src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
                title="Anthony Merante Campaign Video"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            
            {/* Video title below */}
            <p className="text-white text-center mt-4 font-body">
              Anthony Merante for New York State Senate - District 35
            </p>
          </div>
        </div>
      )}
    </>
  );
};

// Quick Donate Section with Slideshow
const QuickDonateSection = ({ galleryPhotos = [] }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Use campaign photos from CAMPAIGN_DATA
  const slideshowPhotos = useMemo(() => {
    return CAMPAIGN_DATA.campaignPhotos.slice(0, 8); // Use first 8 photos
  }, []);
  
  // Use pre-computed face positions (stored in DB) instead of calling analyze-faces API
  // This prevents server overload from OpenCV processing on every page load
  const facePositions = useMemo(() => {
    const positions = {};
    // Default center positions for slideshow photos
    slideshowPhotos.forEach(photo => {
      positions[photo.url] = { x: 50, y: 35 }; // Default center-top positioning
    });
    return positions;
  }, [slideshowPhotos]);
  
  // Auto-advance slideshow
  useEffect(() => {
    if (slideshowPhotos.length === 0) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slideshowPhotos.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [slideshowPhotos.length]);
  
  const currentPhoto = slideshowPhotos[currentSlide];
  const currentFacePosition = currentPhoto ? facePositions[currentPhoto.url] : null;
  
  return (
    <section data-testid="quick-donate" className="py-16 bg-[#000033]">
      <div className="container-campaign">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          {/* Slideshow - Left Side */}
          <div className="relative aspect-[4/3] rounded-lg overflow-hidden shadow-2xl order-2 lg:order-1">
            {slideshowPhotos.length > 0 && currentPhoto && (
              <>
                <SmartFaceImage
                  src={resolveImageUrl(currentPhoto.url)}
                  alt={currentPhoto.caption || 'Campaign photo'}
                  className="w-full h-full"
                  facePosition={currentFacePosition}
                />
                {/* Caption Overlay */}
                {currentPhoto.caption && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                    <p className="text-white text-sm font-medium">{currentPhoto.caption}</p>
                  </div>
                )}
                {/* Slide Indicators */}
                <div className="absolute bottom-4 right-4 flex gap-1">
                  {slideshowPhotos.map((_, idx) => (
                    <button
                      key={`slide-${idx}`}
                      onClick={() => setCurrentSlide(idx)}
                      className={`w-2 h-2 rounded-full transition-colors ${idx === currentSlide ? 'bg-white' : 'bg-white/40'}`}
                      aria-label={`Go to slide ${idx + 1}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
          
          {/* Text and Buttons - Right Side */}
          <div className="order-1 lg:order-2 text-center lg:text-left">
            <h3 className="font-heading text-3xl md:text-4xl text-white uppercase mb-4">
              Support Anthony Today
            </h3>
            <p className="text-gray-300 text-lg mb-8">
              Every dollar helps fight for taxpayers in Albany. Your contribution makes a real difference in our community.
            </p>
            
            {/* Donation Buttons - 2 rows of 3 */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              {CAMPAIGN_DATA.donateAmounts.map((amount) => (
                <a 
                  key={amount}
                  href={`${CAMPAIGN_DATA.donateUrl}?amount=${amount}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-[#3366cc] hover:bg-[#2855b3] text-white font-bold py-4 px-4 text-lg transition-colors text-center rounded"
                  data-testid={`donate-${amount}`}
                >
                  ${amount}
                </a>
              ))}
            </div>
            
            {/* Other Amount Button */}
            <a 
              href={CAMPAIGN_DATA.donateUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="block w-full border-2 border-white text-white hover:bg-white hover:text-[#000033] font-bold py-4 px-6 text-lg transition-colors text-center rounded"
              data-testid="donate-other"
            >
              Other Amount
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Donate CTA Section
const DonateCTA = () => {
  return (
    <section data-testid="donate-cta" className="py-20 bg-[#3366cc]">
      <div className="container-campaign text-center">
        <h2 className="font-heading text-4xl md:text-5xl text-white uppercase mb-6">
          Support the Campaign
        </h2>
        <p className="text-xl text-white/90 font-body mb-8 max-w-2xl mx-auto">
          Help Anthony Merante fight for taxpayers in Albany. Every contribution makes a difference.
        </p>
        
        {/* Quick donate amounts */}
        <div className="flex flex-wrap gap-4 justify-center mb-8">
          {CAMPAIGN_DATA.donateAmounts.map((amount) => (
            <a 
              key={amount}
              href={CAMPAIGN_DATA.donateUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-white text-[#3366cc] hover:bg-gray-100 font-bold py-3 px-8 text-lg transition-colors"
            >
              ${amount}
            </a>
          ))}
        </div>
        
        <a 
          href={CAMPAIGN_DATA.donateUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-block bg-[#000033] text-white font-bold uppercase tracking-wider py-4 px-12 text-lg hover:bg-[#000022] transition-colors shadow-lg"
          data-testid="donate-cta-btn"
        >
          Donate Any Amount
        </a>
      </div>
    </section>
  );
};

// Campaign Photos Section (static photos)
const CampaignPhotosSection = () => {
  const [showAll, setShowAll] = useState(false);
  const displayPhotos = showAll ? CAMPAIGN_DATA.campaignPhotos : CAMPAIGN_DATA.campaignPhotos.slice(0, 9);
  
  return (
    <section data-testid="campaign-photos-section" className="py-20 bg-white">
      <div className="container-campaign">
        <div className="text-center mb-12">
          <h2 className="font-heading text-4xl md:text-5xl text-[#000033] uppercase mb-4">
            On The Campaign Trail
          </h2>
          <p className="text-lg text-gray-600 font-body">
            Anthony Merante connecting with voters across District 35
          </p>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {displayPhotos.map((photo) => (
            <div 
              key={photo.url}
              className="relative group overflow-hidden rounded-sm shadow-lg aspect-square"
            >
              <img 
                src={photo.url}
                alt={photo.caption}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                <p className="text-white font-body text-sm">{photo.caption}</p>
              </div>
            </div>
          ))}
        </div>
        
        {CAMPAIGN_DATA.campaignPhotos.length > 9 && (
          <div className="text-center mt-8">
            <button
              onClick={() => setShowAll(!showAll)}
              className="bg-[#000033] text-white px-8 py-3 font-heading uppercase tracking-wide hover:bg-[#000044] transition-colors inline-flex items-center gap-2"
            >
              <Camera size={20} />
              {showAll ? 'Show Less' : `See All ${CAMPAIGN_DATA.campaignPhotos.length} Photos`}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

// Endorsements Section
const EndorsementsSection = ({ endorsements }) => {
  if (!endorsements || endorsements.length === 0) {
    return null;
  }
  
  const orgEndorsements = endorsements.filter(e => e.endorsement_type === 'organization');
  const individualEndorsements = endorsements.filter(e => e.endorsement_type !== 'organization');
  
  return (
    <section data-testid="endorsements-section" className="py-20 bg-white">
      <div className="container-campaign">
        <div className="text-center mb-12">
          <h2 className="font-heading text-4xl md:text-5xl text-black uppercase mb-4" data-testid="endorsements-title">
            Endorsements
          </h2>
          <p className="text-lg text-gray-600 font-body">
            Leaders and organizations who support Anthony Merante for New York State Senate
          </p>
        </div>
        
        {/* Organization Endorsements */}
        {orgEndorsements.length > 0 && (
          <div className="mb-16">
            <h3 className="font-heading text-2xl text-[#000033] uppercase mb-8 text-center">Endorsed By</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
              {orgEndorsements.map((endorsement, index) => (
                <div 
                  key={endorsement.id || index} 
                  className="bg-gray-50 p-6 rounded-sm card-hover flex flex-col items-center text-center"
                  data-testid={`org-endorsement-${index}`}
                >
                  {endorsement.image_url ? (
                    <img 
                      src={resolveImageUrl(endorsement.image_url)} 
                      alt={endorsement.name}
                      className="h-20 w-auto object-contain mb-4"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded bg-black/10 flex items-center justify-center mb-4">
                      <Building2 className="w-10 h-10 text-black" />
                    </div>
                  )}
                  <h4 className="font-heading text-lg text-black uppercase">{endorsement.name}</h4>
                  {endorsement.title && (
                    <p className="text-gray-500 text-sm mt-1">{endorsement.title}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Individual Endorsements */}
        {individualEndorsements.length > 0 && (
          <div>
            {orgEndorsements.length > 0 && (
              <h3 className="font-heading text-2xl text-black uppercase mb-8 text-center">Individual Endorsements</h3>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {individualEndorsements.map((endorsement, index) => (
                <div 
                  key={endorsement.id || index} 
                  className="bg-gray-50 p-6 rounded-sm card-hover"
                  data-testid={`endorsement-card-${index}`}
                >
                  <div className="flex items-start gap-4">
                    {endorsement.image_url ? (
                      <img 
                        src={resolveImageUrl(endorsement.image_url)} 
                        alt={endorsement.name}
                        className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-black/10 flex items-center justify-center flex-shrink-0">
                        <User className="w-8 h-8 text-black" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-heading text-lg text-black uppercase">{endorsement.name}</h3>
                      {endorsement.title && (
                        <p className="text-campaign-red font-semibold text-sm">{endorsement.title}</p>
                      )}
                      {endorsement.organization && (
                        <p className="text-gray-500 text-sm">{endorsement.organization}</p>
                      )}
                    </div>
                  </div>
                  {endorsement.quote && (
                    <div className="mt-4 relative">
                      <Quote className="w-6 h-6 text-black/20 absolute -top-1 -left-1" />
                      <p className="text-gray-600 font-body italic pl-5">"{endorsement.quote}"</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

// Email/Text Signup Section with Full Form
const SignupSection = () => {
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    zip: '',
    wants_email: true,
    wants_text: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email) return;
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/subscribe`, formData);
      setSubmitStatus('success');
      setFormData({
        email: '',
        first_name: '',
        last_name: '',
        phone: '',
        zip: '',
        wants_email: true,
        wants_text: false
      });
    } catch (err) {
      setSubmitStatus('error');
    }
    setIsSubmitting(false);
  };

  return (
    <section data-testid="signup-section" className="py-20 bg-[#000033]">
      <div className="container-campaign">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="font-heading text-3xl md:text-4xl text-white uppercase mb-4">
              Contact Us
            </h2>
            <p className="text-lg text-gray-300 font-body">
              Sign up to receive campaign updates, event invitations, and ways to get involved.
            </p>
          </div>
          
          {submitStatus === 'success' ? (
            <div className="bg-green-600 text-white p-8 rounded text-center">
              <CheckCircle className="w-16 h-16 mx-auto mb-4" />
              <h3 className="font-heading text-2xl uppercase mb-2">Thank You!</h3>
              <p className="font-body">You've been added to our list. We'll be in touch soon!</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white p-8 rounded-sm shadow-lg" data-testid="signup-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-[#000033] font-semibold mb-2">First Name</label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-[#3366cc]"
                    placeholder="First Name"
                    data-testid="signup-firstname"
                  />
                </div>
                <div>
                  <label className="block text-[#000033] font-semibold mb-2">Last Name</label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-[#3366cc]"
                    placeholder="Last Name"
                    data-testid="signup-lastname"
                  />
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-[#000033] font-semibold mb-2">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-[#3366cc]"
                  placeholder="your@email.com"
                  data-testid="signup-email"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-[#000033] font-semibold mb-2">Phone (optional)</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-[#3366cc]"
                    placeholder="(555) 555-5555"
                    data-testid="signup-phone"
                  />
                </div>
                <div>
                  <label className="block text-[#000033] font-semibold mb-2">ZIP Code (optional)</label>
                  <input
                    type="text"
                    name="zip"
                    value={formData.zip}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-[#3366cc]"
                    placeholder="10701"
                    data-testid="signup-zip"
                  />
                </div>
              </div>
              
              <div className="mb-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="wants_text"
                    checked={formData.wants_text}
                    onChange={handleChange}
                    className="w-5 h-5 text-[#3366cc]"
                  />
                  <span className="text-gray-700">I'd like to receive text message updates</span>
                </label>
              </div>
              
              {submitStatus === 'error' && (
                <p className="text-red-600 mb-4">Something went wrong. Please try again.</p>
              )}
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#3366cc] hover:bg-[#2855b3] text-white font-heading uppercase tracking-wider py-4 text-lg transition-colors disabled:opacity-50"
                data-testid="signup-submit"
              >
                {isSubmitting ? 'Signing Up...' : 'Sign Up'}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

// Home Page
const Home = () => {
  usePageTitle(null); // Use base title for home
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);
  const [endorsements, setEndorsements] = useState([]);
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [issues, setIssues] = useState([]);

  useEffect(() => {
    // Fetch each data source independently to prevent one failure from blocking others
    const fetchEvents = async () => {
      try {
        const res = await axios.get(`${API}/events`);
        setEvents(res.data);
      } catch (err) {
        logError('Home.fetchEvents', err);
      }
    };

    const fetchNews = async () => {
      try {
        const res = await axios.get(`${API}/news`);
        setNews(res.data);
      } catch (err) {
        logError('Home.fetchNews', err);
      }
    };

    const fetchEndorsements = async () => {
      try {
        const res = await axios.get(`${API}/endorsements`);
        setEndorsements(res.data);
      } catch (err) {
        logError('Home.fetchEndorsements', err);
      }
    };

    const fetchGallery = async () => {
      try {
        const res = await axios.get(`${API}/gallery`);
        setGalleryPhotos(res.data);
      } catch (err) {
        logError('Home.fetchGallery', err);
      }
    };

    const fetchIssues = async () => {
      try {
        const res = await axios.get(`${API}/issues`);
        setIssues(res.data);
      } catch (err) {
        logError('Home.fetchIssues', err);
      }
    };

    // Execute all fetches in parallel, each handling its own errors independently
    fetchEvents();
    fetchNews();
    fetchEndorsements();
    fetchGallery();
    fetchIssues();
  }, []);

  return (
    <div data-testid="home-page">
      <HeroSection />
      <VideoSection />
      <QuickDonateSection galleryPhotos={galleryPhotos} />
      <MeetAnthonySection />
      <IssuesSection issues={issues} />
      {galleryPhotos.length > 0 && <GallerySection photos={galleryPhotos} />}
      <EndorsementsSection endorsements={endorsements} />
      <SignupSection />
      <EventsSection events={events} />
      <NewsSection news={news} />
      <DonateCTA />
    </div>
  );
};

// About Page
const AboutPage = () => {
  usePageTitle("About Anthony");
  return (
    <div className="pt-20" data-testid="about-page">
      {/* Hero */}
      <section className="py-20 bg-[#000033]">
        <div className="container-campaign">
          <p className="text-[#3366cc] font-black uppercase tracking-wider mb-2">{CAMPAIGN_DATA.tagline}</p>
          <h1 className="font-heading text-5xl md:text-7xl text-white uppercase">About Anthony</h1>
        </div>
      </section>
      
      {/* Content */}
      <section className="py-20">
        <div className="container-campaign">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <h2 className="font-heading text-3xl text-[#000033] uppercase mb-6">Meet Anthony J. Merante</h2>
              
              <p className="text-lg text-gray-700 font-body leading-relaxed mb-6">
                Anthony J. Merante is an accountant and businessman whose career has centered primarily on accounting, tax services, and executive roles in small and mid-sized companies, particularly in the Yonkers and Westchester County area.
              </p>
              
              <h3 className="font-heading text-2xl text-[#000033] uppercase mb-4">Education and Professional Credentials</h3>
              <ul className="space-y-3 text-gray-600 font-body leading-relaxed mb-6">
                <li className="flex items-start gap-2"><span className="text-[#3366cc] font-bold">•</span> Bachelor of Science in Accounting, St. John's University (1982)</li>
                <li className="flex items-start gap-2"><span className="text-[#3366cc] font-bold">•</span> Became a Certified Public Accountant (CPA) in 1987</li>
                <li className="flex items-start gap-2"><span className="text-[#3366cc] font-bold">•</span> Member of the New York State Society of CPAs since 1988</li>
              </ul>
              <p className="text-gray-600 font-body leading-relaxed mb-6">
                This background established him in the accounting and financial management field early in his career.
              </p>
              
              <h3 className="font-heading text-2xl text-[#000033] uppercase mb-4">Political Career</h3>
              <p className="text-gray-600 font-body leading-relaxed mb-6">
                Merante entered local politics as a community advocate and civic leader. He served for years as president of the Sprain Lake Knolls Civic Association, where he worked on neighborhood issues and local initiatives.
              </p>
              <p className="text-gray-600 font-body leading-relaxed mb-6">
                In 2017, he was elected to the Yonkers City Council for the 6th District and served two terms on the council.
              </p>
              
              <h3 className="font-heading text-2xl text-[#000033] uppercase mb-4">Personal Life</h3>
              <p className="text-gray-600 font-body leading-relaxed mb-8">
                Merante is married to Yolanda Merante, and the couple has two children. The family has deep ties to the Yonkers community, and their children attended Yonkers public schools.
              </p>

              <h3 className="font-heading text-2xl text-[#000033] uppercase mb-4">The Issues</h3>
              
              {/* Energy Bills */}
              <div className="mb-6">
                <div className="flex items-start gap-3 mb-2">
                  <TrendingDown className="w-6 h-6 text-[#3366cc] mt-1 flex-shrink-0" />
                  <div>
                    <strong className="text-[#000033] font-black text-lg">Skyrocketing Energy Bills</strong>
                    <p className="text-gray-600 mt-1">Democrats keep rubber stamping Con Edison rate increases while families struggle to pay their bills. The Governor's Public Service Commission is stacked with energy industry insiders who approve rate hikes while New Yorkers pay the price.</p>
                  </div>
                </div>
              </div>
              
              {/* High Taxes */}
              <div className="mb-6">
                <div className="flex items-start gap-3 mb-2">
                  <TrendingDown className="w-6 h-6 text-[#3366cc] mt-1 flex-shrink-0" />
                  <div>
                    <strong className="text-[#000033] font-black text-lg">High Taxes</strong>
                    <p className="text-gray-600 mt-1">New York is forcing people out. We have some of the highest taxes in the nation. Working families are leaving. Seniors on fixed incomes are being driven out of the communities they spent their lives building. Grandparents are being separated from their families because Albany refuses to control spending.</p>
                  </div>
                </div>
              </div>
              
              {/* Bail Reform */}
              <div className="mb-6">
                <div className="flex items-start gap-3 mb-2">
                  <Shield className="w-6 h-6 text-[#3366cc] mt-1 flex-shrink-0" />
                  <div>
                    <strong className="text-[#000033] font-black text-lg">Albany's Catch and Release Makes Us Less Safe</strong>
                    <p className="text-gray-600 mt-1">So called bail reform has turned our justice system into a revolving door. Criminals are arrested and released right back onto our streets. Families and small businesses are paying the price. Residents no longer feel safe.</p>
                  </div>
                </div>
              </div>
              
              {/* Immigration */}
              <div className="mb-6">
                <div className="flex items-start gap-3 mb-2">
                  <TrendingDown className="w-6 h-6 text-[#3366cc] mt-1 flex-shrink-0" />
                  <div>
                    <strong className="text-[#000033] font-black text-lg">Immigration Costs Skyrocketing</strong>
                    <p className="text-gray-600 mt-1">Albany is spending $4.3 billion of your tax dollars to house illegal migrants in hotels across the state while hardworking New Yorkers struggle to pay rent and property taxes. Meanwhile, our public schools have to beg Albany for funding. This is not right!</p>
                  </div>
                </div>
              </div>
              
              {/* Zoning */}
              <div className="mb-8">
                <div className="flex items-start gap-3 mb-2">
                  <MapPin className="w-6 h-6 text-[#3366cc] mt-1 flex-shrink-0" />
                  <div>
                    <strong className="text-[#000033] font-black text-lg">Takeover of Local Zoning</strong>
                    <p className="text-gray-600 mt-1">Albany wants to override local zoning and build massive housing projects that would overcrowd and destroy neighborhoods. We must stop this Albany power grab. Local governments should decide zoning, not Democratic bureaucrats.</p>
                  </div>
                </div>
              </div>
              
              {/* The Fighter We Need */}
              <div className="bg-[#000033] text-white p-8 rounded-sm mb-8">
                <h3 className="font-heading text-2xl uppercase mb-4">The Fighter Republicans Need in Albany</h3>
                <p className="text-gray-200 leading-relaxed mb-4">
                  Merante is a proven fighter for taxpayers. For eight years as a Yonkers Councilmember, he stood up to the ruling Democratic machine and fought for the people. He voted against tax increases and development deals that would destroy neighborhoods while benefiting political insiders.
                </p>
                <p className="text-gray-200 leading-relaxed">
                  His campaign is about standing up for taxpayers, homeowners, renters, seniors, and working families who feel like no one in government is fighting for them anymore. Government should work for the people, not special interests or political insiders.
                </p>
              </div>
            </div>
            
            <div>
              <img 
                src="/merante-portrait.jpg"
                alt="Anthony Merante" 
                className="w-full rounded-sm shadow-lg mb-6"
              />
              <img 
                src="/merante-family.png"
                alt="Anthony Merante with his family" 
                className="w-full rounded-sm shadow-lg mb-6"
              />
              <img 
                src="/merante-news12.jpg"
                alt="Anthony Merante speaking at News 12" 
                className="w-full rounded-sm shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>
      
      <DonateCTA />
    </div>
  );
};

// Events Page
const EventsPage = () => {
  usePageTitle("Events");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await axios.get(`${API}/events`);
        setEvents(res.data);
      } catch (err) {
        logError("EventsPage.fetchEvents", err);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  return (
    <div className="pt-28" data-testid="events-page">
      <section className="py-20 bg-[#000033]">
        <div className="container-campaign">
          <h1 className="font-heading text-5xl md:text-7xl text-white uppercase">Events</h1>
        </div>
      </section>
      
      <EventsSection events={events} showAll={true} />
      <DonateCTA />
    </div>
  );
};

// News Page
const NewsPage = () => {
  usePageTitle("In The News");
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const res = await axios.get(`${API}/news`);
        setNews(res.data);
      } catch (err) {
        logError("NewsPage.fetchNews", err);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, []);

  return (
    <div className="pt-28" data-testid="news-page">
      <section className="py-20 bg-[#000033]">
        <div className="container-campaign">
          <h1 className="font-heading text-5xl md:text-7xl text-white uppercase">In The News</h1>
        </div>
      </section>
      
      <NewsSection news={news} showAll={true} />
      <DonateCTA />
    </div>
  );
};

// Admin Login Page
const AdminLogin = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${API}/admin/verify`, {}, {
        auth: { username, password }
      });
      onLogin({ username, password });
    } catch (e) {
      setError('Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100" data-testid="admin-login">
      <div className="bg-white p-8 rounded-sm shadow-lg w-full max-w-md">
        <div className="text-center mb-8">
          <img src={CAMPAIGN_DATA.logoDark} alt="Logo" className="h-20 mx-auto mb-4" />
          <h1 className="font-heading text-2xl text-[#000033] uppercase">Admin Login</h1>
        </div>
        
        <form onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}
          
          <div className="mb-4">
            <label className="block text-gray-700 font-black mb-2">Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
              required
              data-testid="admin-username"
            />
          </div>
          
          <div className="mb-6">
            <label className="block text-gray-700 font-black mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
              required
              data-testid="admin-password"
            />
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-black text-white font-black uppercase py-3 hover:bg-black-light transition-colors disabled:opacity-50"
            data-testid="admin-submit"
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
        
        <div className="mt-4 text-center">
          <Link to="/" className="text-gray-500 hover:text-black font-semibold">
            &larr; Back to Website
          </Link>
        </div>
      </div>
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = ({ auth, onLogout }) => {
  const [activeTab, setActiveTab] = useState('events');
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);
  const [endorsements, setEndorsements] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [issues, setIssues] = useState([]);
  const [media, setMedia] = useState([]);
  const [galleryDisplayMode, setGalleryDisplayMode] = useState('manual');
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const authConfig = useMemo(() => ({ 
    auth: { username: auth.username, password: auth.password } 
  }), [auth.username, auth.password]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [eventsRes, newsRes, endorsementsRes, subscribersRes, galleryRes, issuesRes, mediaRes, gallerySettingRes] = await Promise.all([
        axios.get(`${API}/admin/events`, authConfig),
        axios.get(`${API}/admin/news`, authConfig),
        axios.get(`${API}/admin/endorsements`, authConfig),
        axios.get(`${API}/admin/subscribers`, authConfig),
        axios.get(`${API}/admin/gallery`, authConfig),
        axios.get(`${API}/admin/issues`, authConfig),
        axios.get(`${API}/admin/media`, authConfig),
        axios.get(`${API}/admin/settings/gallery_display`, authConfig)
      ]);
      setEvents(eventsRes.data);
      setNews(newsRes.data);
      setEndorsements(endorsementsRes.data);
      setSubscribers(subscribersRes.data);
      setGallery(galleryRes.data);
      setIssues(issuesRes.data);
      setMedia(mediaRes.data);
      setGalleryDisplayMode(gallerySettingRes.data.value || 'manual');
    } catch (err) {
      logError("AdminDashboard.fetchData", err);
    } finally {
      setLoading(false);
    }
  }, [authConfig]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateGalleryDisplayMode = async (mode) => {
    try {
      await axios.put(`${API}/admin/settings/gallery_display`, { value: mode }, authConfig);
      setGalleryDisplayMode(mode);
    } catch (err) {
      logError("updateGalleryDisplayMode", err);
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      await axios.delete(`${API}/admin/${type}/${id}`, authConfig);
      fetchData();
    } catch (err) {
      logError("deleteItem", err);
    }
  };

  const handleSave = async (type, data) => {
    try {
      if (editItem) {
        await axios.put(`${API}/admin/${type}/${editItem.id}`, data, authConfig);
      } else {
        await axios.post(`${API}/admin/${type}`, data, authConfig);
      }
      fetchData();
      setShowForm(false);
      setEditItem(null);
    } catch (err) {
      logError("saveItem", err);
    }
  };

  const exportSubscribers = () => {
    const csv = [
      ['Email', 'First Name', 'Last Name', 'Phone', 'Wants Email', 'Wants Text', 'Subscribed At'].join(','),
      ...subscribers.map(s => [
        s.email,
        s.first_name || '',
        s.last_name || '',
        s.phone || '',
        s.wants_email ? 'Yes' : 'No',
        s.wants_text ? 'Yes' : 'No',
        s.subscribed_at
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `subscribers_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="min-h-screen bg-gray-100" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-[#000033] text-white py-4 px-6 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <img src={CAMPAIGN_DATA.logoWhite} alt="Logo" className="h-12" />
          <h1 className="font-heading text-xl uppercase">Admin Dashboard</h1>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-gray-300 font-semibold">Welcome, {auth.username}</span>
          <button onClick={onLogout} className="bg-[#3366cc] px-4 py-2 text-sm font-black hover:bg-[#2855b3] transition-colors" data-testid="admin-logout">
            Logout
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="p-6">
        {/* Tabs */}
        <div className="flex gap-4 mb-6 flex-wrap">
          <button
            onClick={() => { setActiveTab('events'); setShowForm(false); setEditItem(null); }}
            className={`px-6 py-3 font-semibold uppercase ${activeTab === 'events' ? 'bg-[#000033] text-white' : 'bg-white text-[#000033]'}`}
            data-testid="admin-events-tab"
          >
            Events
          </button>
          <button
            onClick={() => { setActiveTab('news'); setShowForm(false); setEditItem(null); }}
            className={`px-6 py-3 font-semibold uppercase ${activeTab === 'news' ? 'bg-[#000033] text-white' : 'bg-white text-[#000033]'}`}
            data-testid="admin-news-tab"
          >
            News
          </button>
          <button
            onClick={() => { setActiveTab('endorsements'); setShowForm(false); setEditItem(null); }}
            className={`px-6 py-3 font-semibold uppercase ${activeTab === 'endorsements' ? 'bg-[#000033] text-white' : 'bg-white text-[#000033]'}`}
            data-testid="admin-endorsements-tab"
          >
            Endorsements
          </button>
          <button
            onClick={() => { setActiveTab('gallery'); setShowForm(false); setEditItem(null); }}
            className={`px-6 py-3 font-semibold uppercase flex items-center gap-2 ${activeTab === 'gallery' ? 'bg-[#000033] text-white' : 'bg-white text-[#000033]'}`}
            data-testid="admin-gallery-tab"
          >
            <Image size={18} /> Gallery ({gallery.length})
          </button>
          <button
            onClick={() => { setActiveTab('issues'); setShowForm(false); setEditItem(null); }}
            className={`px-6 py-3 font-semibold uppercase flex items-center gap-2 ${activeTab === 'issues' ? 'bg-[#000033] text-white' : 'bg-white text-[#000033]'}`}
            data-testid="admin-issues-tab"
          >
            <Shield size={18} /> Issues ({issues.length})
          </button>
          <button
            onClick={() => { setActiveTab('subscribers'); setShowForm(false); setEditItem(null); }}
            className={`px-6 py-3 font-semibold uppercase flex items-center gap-2 ${activeTab === 'subscribers' ? 'bg-[#000033] text-white' : 'bg-white text-[#000033]'}`}
            data-testid="admin-subscribers-tab"
          >
            <Users size={18} /> Subscribers ({subscribers.length})
          </button>
          <button
            onClick={() => { setActiveTab('media'); setShowForm(false); setEditItem(null); }}
            className={`px-6 py-3 font-semibold uppercase flex items-center gap-2 ${activeTab === 'media' ? 'bg-[#000033] text-white' : 'bg-white text-[#000033]'}`}
            data-testid="admin-media-tab"
          >
            <FileText size={18} /> Media ({media.length})
          </button>
          <button
            onClick={() => { setActiveTab('settings'); setShowForm(false); setEditItem(null); }}
            className={`px-6 py-3 font-semibold uppercase flex items-center gap-2 ${activeTab === 'settings' ? 'bg-[#000033] text-white' : 'bg-white text-[#000033]'}`}
            data-testid="admin-settings-tab"
          >
            <Settings size={18} /> Settings
          </button>
        </div>

        {/* Add Button (not for subscribers, media, or settings) */}
        {activeTab !== 'subscribers' && activeTab !== 'settings' && activeTab !== 'media' && (
          <div className="mb-6">
            <button
              onClick={() => { setShowForm(true); setEditItem(null); }}
              className="bg-[#3366cc] text-white px-6 py-3 font-semibold uppercase hover:bg-[#2855b3] transition-colors"
              data-testid="admin-add-btn"
            >
              Add {activeTab === 'events' ? 'Event' : activeTab === 'news' ? 'News' : activeTab === 'gallery' ? 'Photo' : activeTab === 'issues' ? 'Issue' : 'Endorsement'}
            </button>
          </div>
        )}

        {/* Export Button for subscribers */}
        {activeTab === 'subscribers' && subscribers.length > 0 && (
          <div className="mb-6">
            <button
              onClick={exportSubscribers}
              className="bg-green-600 text-white px-6 py-3 font-semibold uppercase hover:bg-green-700 transition-colors flex items-center gap-2"
              data-testid="admin-export-btn"
            >
              <Download size={18} /> Export CSV
            </button>
          </div>
        )}

        {/* Form */}
        {showForm && (
          <div className="bg-white p-6 rounded-sm shadow mb-6" data-testid="admin-form">
            {activeTab === 'events' ? (
              <EventForm 
                event={editItem} 
                onSave={(data) => handleSave('events', data)} 
                onCancel={() => { setShowForm(false); setEditItem(null); }} 
              />
            ) : activeTab === 'news' ? (
              <NewsForm 
                article={editItem} 
                onSave={(data) => handleSave('news', data)} 
                onCancel={() => { setShowForm(false); setEditItem(null); }}
                authConfig={authConfig}
              />
            ) : activeTab === 'gallery' ? (
              <GalleryForm 
                photo={editItem} 
                onSave={(data) => handleSave('gallery', data)} 
                onCancel={() => { setShowForm(false); setEditItem(null); }}
                authConfig={authConfig}
                onBulkSave={async (photos) => {
                  for (const photo of photos) {
                    await axios.post(`${API}/admin/gallery`, photo, authConfig);
                  }
                  fetchData();
                }}
              />
            ) : activeTab === 'issues' ? (
              <IssueForm 
                issue={editItem} 
                onSave={(data) => handleSave('issues', data)} 
                onCancel={() => { setShowForm(false); setEditItem(null); }}
              />
            ) : (
              <EndorsementForm 
                endorsement={editItem} 
                onSave={(data) => handleSave('endorsements', data)} 
                onCancel={() => { setShowForm(false); setEditItem(null); }}
                authConfig={authConfig}
              />
            )}
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-sm shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : activeTab === 'events' ? (
            <table className="w-full" data-testid="admin-events-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {events.map((event) => (
                  <tr key={event.id}>
                    <td className="px-6 py-4">{event.title}</td>
                    <td className="px-6 py-4">{event.date}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded ${event.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {event.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button onClick={() => { setEditItem(event); setShowForm(true); }} className="text-black hover:underline mr-4">Edit</button>
                      <button onClick={() => handleDelete('events', event.id)} className="text-red-600 hover:underline">Delete</button>
                    </td>
                  </tr>
                ))}
                {events.length === 0 && (
                  <tr><td colSpan="4" className="px-6 py-8 text-center text-gray-500">No events found</td></tr>
                )}
              </tbody>
            </table>
          ) : activeTab === 'news' ? (
            <table className="w-full" data-testid="admin-news-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Source</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {news.map((article) => (
                  <tr key={article.id}>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded ${article.content_type === 'original' ? 'bg-campaign-red/10 text-campaign-red' : 'bg-blue-100 text-blue-800'}`}>
                        {article.content_type === 'original' ? 'Original' : 'External'}
                      </span>
                      {article.video_url && <span className="ml-1 text-xs">🎬</span>}
                    </td>
                    <td className="px-6 py-4 max-w-xs truncate">{article.title}</td>
                    <td className="px-6 py-4">{article.content_type === 'original' ? 'Campaign' : (article.source || '-')}</td>
                    <td className="px-6 py-4">{article.published_date}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded ${article.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {article.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button onClick={() => { setEditItem(article); setShowForm(true); }} className="text-black hover:underline mr-4">Edit</button>
                      <button onClick={() => handleDelete('news', article.id)} className="text-red-600 hover:underline">Delete</button>
                    </td>
                  </tr>
                ))}
                {news.length === 0 && (
                  <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500">No news articles found</td></tr>
                )}
              </tbody>
            </table>
          ) : activeTab === 'endorsements' ? (
            <table className="w-full" data-testid="admin-endorsements-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Logo/Image</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {endorsements.map((endorsement) => (
                  <tr key={endorsement.id}>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded ${endorsement.endorsement_type === 'organization' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                        {endorsement.endorsement_type === 'organization' ? 'Org' : 'Individual'}
                      </span>
                    </td>
                    <td className="px-6 py-4">{endorsement.name}</td>
                    <td className="px-6 py-4">{endorsement.title || '-'}</td>
                    <td className="px-6 py-4">
                      {endorsement.image_url ? (
                        <img src={resolveImageUrl(endorsement.image_url)} alt="" className="w-10 h-10 object-contain rounded" />
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded ${endorsement.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {endorsement.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button onClick={() => { setEditItem(endorsement); setShowForm(true); }} className="text-black hover:underline mr-4">Edit</button>
                      <button onClick={() => handleDelete('endorsements', endorsement.id)} className="text-red-600 hover:underline">Delete</button>
                    </td>
                  </tr>
                ))}
                {endorsements.length === 0 && (
                  <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500">No endorsements found</td></tr>
                )}
              </tbody>
            </table>
          ) : activeTab === 'gallery' ? (
            <div className="p-6">
              {/* Display Mode Selector */}
              <div className="mb-6 p-4 bg-gray-50 rounded flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-gray-700">Display Order:</span>
                  <select
                    value={galleryDisplayMode}
                    onChange={(e) => updateGalleryDisplayMode(e.target.value)}
                    className="border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
                    data-testid="gallery-display-mode"
                  >
                    <option value="manual">Manual (by order number)</option>
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="random">Random (shuffle)</option>
                  </select>
                </div>
                <p className="text-sm text-gray-500">
                  {galleryDisplayMode === 'manual' && 'Photos will display in the order you set (edit each photo to change order number)'}
                  {galleryDisplayMode === 'newest' && 'Most recently added photos will show first'}
                  {galleryDisplayMode === 'oldest' && 'Oldest photos will show first'}
                  {galleryDisplayMode === 'random' && 'Photos will be shuffled randomly on each page load'}
                </p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {gallery.map((photo) => (
                  <div key={photo.id} className="relative group bg-gray-100 rounded overflow-hidden" data-testid={`gallery-admin-${photo.id}`}>
                    <img 
                      src={resolveImageUrl(photo.url)} 
                      alt={photo.caption || 'Gallery photo'}
                      className="w-full aspect-square object-cover"
                    />
                    {galleryDisplayMode === 'manual' && (
                      <div className="absolute top-2 left-2 bg-black text-white text-xs px-2 py-1 rounded font-semibold">
                        #{photo.order || 0}
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-2">
                      <p className="text-white text-xs text-center line-clamp-2">{photo.caption || 'No caption'}</p>
                      <div className="flex gap-2">
                        <button 
                          onClick={() => { setEditItem(photo); setShowForm(true); }} 
                          className="bg-white text-black px-3 py-1 text-xs font-semibold rounded hover:bg-gray-100"
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => handleDelete('gallery', photo.id)} 
                          className="bg-red-600 text-white px-3 py-1 text-xs font-semibold rounded hover:bg-red-700"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {gallery.length === 0 && (
                  <div className="col-span-full text-center py-12 text-gray-500">
                    <Image className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                    <p>No photos in gallery. Click "Add Photo" to get started.</p>
                  </div>
                )}
              </div>
            </div>
          ) : activeTab === 'issues' ? (
            <table className="w-full" data-testid="admin-issues-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Order</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Icon</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Description</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {issues.map((issue) => {
                  const IconComponent = ICON_MAP[issue.icon] || Shield;
                  return (
                    <tr key={issue.id}>
                      <td className="px-6 py-4 font-semibold">{issue.order}</td>
                      <td className="px-6 py-4">
                        <div className="w-10 h-10 rounded bg-black/10 flex items-center justify-center">
                          <IconComponent className="w-5 h-5 text-black" />
                        </div>
                      </td>
                      <td className="px-6 py-4 font-semibold">{issue.title}</td>
                      <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">{issue.description}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded ${issue.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {issue.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button onClick={() => { setEditItem(issue); setShowForm(true); }} className="text-black hover:underline mr-4">Edit</button>
                        <button onClick={() => handleDelete('issues', issue.id)} className="text-red-600 hover:underline">Delete</button>
                      </td>
                    </tr>
                  );
                })}
                {issues.length === 0 && (
                  <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500">No issues found. Click "Add Issue" to create platform issues.</td></tr>
                )}
              </tbody>
            </table>
          ) : activeTab === 'subscribers' ? (
            <table className="w-full" data-testid="admin-subscribers-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Phone</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Preferences</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Subscribed</th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {subscribers.map((subscriber) => (
                  <tr key={subscriber.id}>
                    <td className="px-6 py-4">{subscriber.email}</td>
                    <td className="px-6 py-4">{subscriber.first_name || subscriber.last_name ? `${subscriber.first_name || ''} ${subscriber.last_name || ''}`.trim() : '-'}</td>
                    <td className="px-6 py-4">{subscriber.phone || '-'}</td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        {subscriber.wants_email && <span className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-800">Email</span>}
                        {subscriber.wants_text && <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-800">Text</span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{new Date(subscriber.subscribed_at).toLocaleDateString()}</td>
                    <td className="px-6 py-4 text-right">
                      <button onClick={() => handleDelete('subscribers', subscriber.id)} className="text-red-600 hover:underline">Delete</button>
                    </td>
                  </tr>
                ))}
                {subscribers.length === 0 && (
                  <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500">No subscribers yet</td></tr>
                )}
              </tbody>
            </table>
          ) : activeTab === 'media' ? (
            <MediaPanel auth={auth} media={media} onRefresh={fetchData} />
          ) : activeTab === 'settings' ? (
            <SettingsPanel auth={auth} />
          ) : null}
        </div>
      </div>
    </div>
  );
};

// Media Panel Component - Upload and manage campaign materials
const MediaPanel = ({ auth, media, onRefresh }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const authConfig = { auth: { username: auth.username, password: auth.password } };

  const getFileType = (file) => {
    const mimeType = file.type;
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType === 'application/pdf') return 'pdf';
    return 'document';
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'image': return <Image size={24} className="text-blue-500" />;
      case 'pdf': return <FileText size={24} className="text-red-500" />;
      default: return <File size={24} className="text-gray-500" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setUploadProgress({ current: 0, total: files.length });

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const formData = new FormData();
        formData.append('file', file);

        const uploadRes = await axios.post(`${API}/upload`, formData, {
          ...authConfig,
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        const mediaData = {
          name: file.name,
          file_url: uploadRes.data.url,
          file_type: getFileType(file),
          file_size: file.size,
          mime_type: file.type
        };

        await axios.post(`${API}/admin/media`, mediaData, authConfig);
        setUploadProgress({ current: i + 1, total: files.length });
      } catch (err) {
        logError("uploadFile", err);
      }
    }

    setUploading(false);
    setUploadProgress(null);
    onRefresh();
    e.target.value = '';
  };

  const handleDelete = async (mediaId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;
    try {
      await axios.delete(`${API}/admin/media/${mediaId}`, authConfig);
      onRefresh();
    } catch (err) {
      logError("deleteMedia", err);
    }
  };

  const handleView = (item) => {
    window.open(resolveImageUrl(item.file_url), '_blank');
  };

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      <div className="bg-gray-50 p-6 rounded-lg border-2 border-dashed border-gray-300">
        <div className="text-center">
          <FileText size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Upload Campaign Materials</h3>
          <p className="text-gray-500 mb-4">Upload flyers, brochures, PDFs, and other documents</p>
          <label className="inline-block bg-[#3366cc] text-white px-6 py-3 font-semibold uppercase cursor-pointer hover:bg-[#2855b3] transition-colors">
            {uploading ? (
              `Uploading ${uploadProgress?.current}/${uploadProgress?.total}...`
            ) : (
              'Select Files'
            )}
            <input
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
              onChange={handleUpload}
              className="hidden"
              disabled={uploading}
            />
          </label>
          <p className="text-sm text-gray-400 mt-2">Supports: Images, PDFs, Word, Excel, PowerPoint</p>
        </div>
      </div>

      {/* Media Grid */}
      {media.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <FileText size={64} className="mx-auto mb-4 opacity-50" />
          <p>No media files uploaded yet. Click "Select Files" to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {media.map((item) => (
            <div key={item.id} className="bg-white border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow group">
              <div className="aspect-square bg-gray-100 flex items-center justify-center relative">
                {item.file_type === 'image' ? (
                  <img 
                    src={resolveImageUrl(item.file_url)} 
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center p-4">
                    {getFileIcon(item.file_type)}
                    <span className="text-xs text-gray-500 mt-2 uppercase">
                      {item.file_type}
                    </span>
                  </div>
                )}
                {/* Hover Actions */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button
                    onClick={() => handleView(item)}
                    className="p-2 bg-white rounded-full hover:bg-gray-100"
                    title="View/Download"
                  >
                    <Eye size={18} className="text-gray-700" />
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="p-2 bg-white rounded-full hover:bg-red-50"
                    title="Delete"
                  >
                    <Trash2 size={18} className="text-red-500" />
                  </button>
                </div>
              </div>
              <div className="p-2">
                <p className="text-sm font-medium text-gray-800 truncate" title={item.name}>
                  {item.name}
                </p>
                <p className="text-xs text-gray-500">
                  {formatFileSize(item.file_size)}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Settings Panel Component - Constant Contact Integration & Custom CTA
const SettingsPanel = ({ auth }) => {
  const [ccSettings, setCcSettings] = useState({
    api_key: '',
    access_token: '',
    list_id: ''
  });
  const [ccStatus, setCcStatus] = useState({ configured: false, access_token_last4: null });
  const [customCta, setCustomCta] = useState({ enabled: false, text: '', url: '' });
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingCta, setIsSavingCta] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [message, setMessage] = useState(null);
  const [ctaMessage, setCtaMessage] = useState(null);

  const authConfig = { auth: { username: auth.username, password: auth.password } };

  useEffect(() => {
    fetchCCSettings();
    fetchCustomCta();
  }, []);

  const fetchCustomCta = async () => {
    try {
      const res = await axios.get(`${API}/admin/settings/custom_cta`, authConfig);
      if (res.data.value) {
        setCustomCta(res.data.value);
      }
    } catch (err) {
      logError("fetchCustomCta", err);
    }
  };

  const handleSaveCta = async (e) => {
    e.preventDefault();
    setIsSavingCta(true);
    setCtaMessage(null);
    
    try {
      await axios.put(`${API}/admin/settings/custom_cta`, { 
        value: JSON.stringify(customCta)
      }, authConfig);
      setCtaMessage({ type: 'success', text: 'Custom CTA button saved successfully!' });
    } catch (err) {
      setCtaMessage({ type: 'error', text: 'Failed to save custom CTA settings.' });
    }
    setIsSavingCta(false);
  };

  const fetchCCSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/constantcontact-settings`, authConfig);
      setCcStatus(res.data);
      if (res.data.list_id) {
        setCcSettings(prev => ({
          ...prev,
          list_id: res.data.list_id
        }));
      }
    } catch (err) {
      // Error fetching Constant Contact settings
    }
  };

  const handleSaveCC = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setMessage(null);
    
    try {
      await axios.post(`${API}/admin/constantcontact-settings`, ccSettings, authConfig);
      setMessage({ type: 'success', text: 'Constant Contact settings saved successfully!' });
      fetchCCSettings();
      setCcSettings(prev => ({ ...prev, api_key: '', access_token: '' })); // Clear tokens after save
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Failed to save settings' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSyncCC = async () => {
    setIsSyncing(true);
    setMessage(null);
    
    try {
      const res = await axios.post(`${API}/admin/constantcontact-sync`, {}, authConfig);
      setMessage({ type: 'success', text: `${res.data.message}${res.data.errors?.length > 0 ? ` (${res.data.errors.length} errors)` : ''}` });
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Sync failed' });
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <div className="p-6 max-w-2xl">
      <h2 className="font-heading text-2xl text-[#000033] uppercase mb-6">Settings</h2>
      
      {/* Constant Contact Settings */}
      <div className="bg-white p-6 rounded shadow-sm mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl text-[#000033] uppercase">Constant Contact Integration</h3>
          {ccStatus.configured && (
            <span className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full">
              Connected (***{ccStatus.access_token_last4})
            </span>
          )}
        </div>
        
        <p className="text-gray-600 mb-4">
          Connect your Constant Contact account to automatically sync new subscribers to your email list.
        </p>
        
        {message && (
          <div className={`p-4 rounded mb-4 ${message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
            {message.text}
          </div>
        )}
        
        <form onSubmit={handleSaveCC} className="space-y-4">
          <div>
            <label className="block text-gray-700 font-semibold mb-2">API Key</label>
            <input
              type="password"
              value={ccSettings.api_key}
              onChange={(e) => setCcSettings({...ccSettings, api_key: e.target.value})}
              className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-[#3366cc]"
              placeholder={ccStatus.configured ? '••••••••••••' : 'Enter your Constant Contact API key'}
            />
            <p className="text-sm text-gray-500 mt-1">
              Find your API key in Constant Contact: My Account → My API Keys
            </p>
          </div>
          
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Access Token</label>
            <input
              type="password"
              value={ccSettings.access_token}
              onChange={(e) => setCcSettings({...ccSettings, access_token: e.target.value})}
              className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-[#3366cc]"
              placeholder={ccStatus.configured ? '••••••••••••' : 'Enter your access token'}
            />
            <p className="text-sm text-gray-500 mt-1">
              Generate an access token from the Constant Contact API dashboard
            </p>
          </div>
          
          <div>
            <label className="block text-gray-700 font-semibold mb-2">List ID</label>
            <input
              type="text"
              value={ccSettings.list_id}
              onChange={(e) => setCcSettings({...ccSettings, list_id: e.target.value})}
              className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-[#3366cc]"
              placeholder="e.g., 12345678-abcd-1234-efgh-123456789012"
            />
            <p className="text-sm text-gray-500 mt-1">
              Find in Constant Contact: Contacts → Lists → Click list → Copy ID from URL
            </p>
          </div>
          
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={isSaving}
              className="bg-[#3366cc] text-white px-6 py-2 font-semibold uppercase hover:bg-[#2855b3] transition-colors disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Save Settings'}
            </button>
            
            {ccStatus.configured && (
              <button
                type="button"
                onClick={handleSyncCC}
                disabled={isSyncing}
                className="bg-green-600 text-white px-6 py-2 font-semibold uppercase hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {isSyncing ? 'Syncing...' : 'Sync All Subscribers'}
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Custom CTA Button Settings */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Custom Hero Button</h3>
        <p className="text-gray-600 mb-4">
          Add a third call-to-action button to the hero section. Great for promoting fundraisers, events, or special campaigns.
        </p>
        
        {ctaMessage && (
          <div className={`p-4 rounded mb-4 ${ctaMessage.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
            {ctaMessage.text}
          </div>
        )}
        
        <form onSubmit={handleSaveCta} className="space-y-4">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="cta-enabled"
              checked={customCta.enabled}
              onChange={(e) => setCustomCta({...customCta, enabled: e.target.checked})}
              className="w-5 h-5 text-[#3366cc] rounded focus:ring-[#3366cc]"
            />
            <label htmlFor="cta-enabled" className="text-gray-700 font-semibold">
              Enable Custom Button
            </label>
          </div>
          
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Button Text</label>
            <input
              type="text"
              value={customCta.text}
              onChange={(e) => setCustomCta({...customCta, text: e.target.value})}
              className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-[#3366cc]"
              placeholder="e.g., Spring Fundraiser"
              disabled={!customCta.enabled}
            />
          </div>
          
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Button URL</label>
            <input
              type="url"
              value={customCta.url}
              onChange={(e) => setCustomCta({...customCta, url: e.target.value})}
              className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-[#3366cc]"
              placeholder="e.g., https://example.com/fundraiser"
              disabled={!customCta.enabled}
            />
            <p className="text-sm text-gray-500 mt-1">
              Enter the full URL where the button should link to
            </p>
          </div>
          
          <button
            type="submit"
            disabled={isSavingCta}
            className="bg-[#3366cc] text-white px-6 py-2 font-semibold uppercase hover:bg-[#2855b3] transition-colors disabled:opacity-50"
          >
            {isSavingCta ? 'Saving...' : 'Save Button'}
          </button>
        </form>
      </div>
    </div>
  );
};

// Event Form
const EventForm = ({ event, onSave, onCancel }) => {
  const [form, setForm] = useState({
    title: event?.title || '',
    description: event?.description || '',
    date: event?.date || 'TBD',
    time: event?.time || '',
    location: event?.location || '',
    rsvp_link: event?.rsvp_link || '#',
    image_url: event?.image_url || '',
    is_active: event?.is_active ?? true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <form onSubmit={handleSubmit}>
      <h3 className="font-heading text-xl text-black uppercase mb-4">{event ? 'Edit Event' : 'Add Event'}</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Title *</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({...form, title: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
            required
          />
        </div>
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Date *</label>
          <input
            type="text"
            value={form.date}
            onChange={(e) => setForm({...form, date: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
            placeholder="e.g., TBD, January 15, 2026"
            required
          />
        </div>
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Time</label>
          <input
            type="text"
            value={form.time}
            onChange={(e) => setForm({...form, time: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
            placeholder="e.g., 6:00 PM"
          />
        </div>
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Location</label>
          <input
            type="text"
            value={form.location}
            onChange={(e) => setForm({...form, location: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
          />
        </div>
        <div>
          <label className="block text-gray-700 font-semibold mb-2">RSVP Link</label>
          <input
            type="text"
            value={form.rsvp_link}
            onChange={(e) => setForm({...form, rsvp_link: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
            placeholder="Google Form URL or #"
          />
        </div>
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Image URL</label>
          <input
            type="text"
            value={form.image_url}
            onChange={(e) => setForm({...form, image_url: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
          />
        </div>
      </div>
      
      <div className="mb-4">
        <label className="block text-gray-700 font-semibold mb-2">Description *</label>
        <textarea
          value={form.description}
          onChange={(e) => setForm({...form, description: e.target.value})}
          className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
          rows="3"
          required
        />
      </div>
      
      <div className="mb-6">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setForm({...form, is_active: e.target.checked})}
            className="w-5 h-5"
          />
          <span className="text-gray-700 font-semibold">Active (visible on website)</span>
        </label>
      </div>
      
      <div className="flex gap-4">
        <button type="submit" className="bg-black text-white px-6 py-2 font-semibold hover:bg-black-light transition-colors">
          Save
        </button>
        <button type="button" onClick={onCancel} className="bg-gray-300 text-gray-700 px-6 py-2 font-semibold hover:bg-gray-400 transition-colors">
          Cancel
        </button>
      </div>
    </form>
  );
};

// News Form
const NewsForm = ({ article, onSave, onCancel, authConfig }) => {
  const [form, setForm] = useState({
    title: article?.title || '',
    summary: article?.summary || '',
    content: article?.content || '',
    content_type: article?.content_type || 'external',
    source: article?.source || '',
    link: article?.link || '',
    image_url: article?.image_url || '',
    video_url: article?.video_url || '',
    published_date: article?.published_date || new Date().toISOString().split('T')[0],
    is_active: article?.is_active ?? true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  // Extract YouTube video ID for preview
  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return match ? match[1] : null;
  };

  const videoId = getYouTubeId(form.video_url);

  return (
    <form onSubmit={handleSubmit}>
      <h3 className="font-heading text-xl text-black uppercase mb-4">{article ? 'Edit News' : 'Add News'}</h3>
      
      {/* Content Type Selector */}
      <div className="mb-6 p-4 bg-gray-50 rounded">
        <label className="block text-gray-700 font-semibold mb-3">Post Type</label>
        <div className="flex gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="content_type"
              value="external"
              checked={form.content_type === 'external'}
              onChange={(e) => setForm({...form, content_type: e.target.value})}
              className="w-4 h-4"
            />
            <span className="font-semibold">External Link</span>
            <span className="text-gray-500 text-sm">(Link to news article)</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="content_type"
              value="original"
              checked={form.content_type === 'original'}
              onChange={(e) => setForm({...form, content_type: e.target.value})}
              className="w-4 h-4"
            />
            <span className="font-semibold">Original Post</span>
            <span className="text-gray-500 text-sm">(Your own content/video)</span>
          </label>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Title *</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({...form, title: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
            required
          />
        </div>
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Published Date *</label>
          <input
            type="date"
            value={form.published_date}
            onChange={(e) => setForm({...form, published_date: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
            required
          />
        </div>
        
        {form.content_type === 'external' ? (
          <>
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Source *</label>
              <input
                type="text"
                value={form.source}
                onChange={(e) => setForm({...form, source: e.target.value})}
                className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
                placeholder="e.g., Yonkers Tribune"
                required={form.content_type === 'external'}
              />
            </div>
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Article URL *</label>
              <input
                type="url"
                value={form.link}
                onChange={(e) => setForm({...form, link: e.target.value})}
                className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
                placeholder="https://..."
                required={form.content_type === 'external'}
              />
            </div>
          </>
        ) : (
          <div className="md:col-span-2">
            <label className="block text-gray-700 font-semibold mb-2">Video URL (YouTube)</label>
            <input
              type="url"
              value={form.video_url}
              onChange={(e) => setForm({...form, video_url: e.target.value})}
              className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
              placeholder="https://www.youtube.com/watch?v=..."
            />
            {videoId && (
              <div className="mt-3">
                <p className="text-sm text-gray-500 mb-2">Preview:</p>
                <div className="aspect-video w-64 bg-black rounded overflow-hidden">
                  <iframe
                    src={`https://www.youtube.com/embed/${videoId}`}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title="Video preview"
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Featured Image */}
      <div className="mb-4">
        <ImageUpload 
          value={form.image_url}
          onChange={(url) => setForm({...form, image_url: url})}
          authConfig={authConfig}
          label="Featured Image"
        />
      </div>
      
      <div className="mb-4">
        <label className="block text-gray-700 font-semibold mb-2">
          {form.content_type === 'external' ? 'Summary *' : 'Summary / Excerpt *'}
        </label>
        <textarea
          value={form.summary}
          onChange={(e) => setForm({...form, summary: e.target.value})}
          className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
          rows="3"
          required
          placeholder={form.content_type === 'external' ? 'Brief summary of the article...' : 'Short excerpt shown in the news list...'}
        />
      </div>
      
      {/* Full Content for Original Posts */}
      {form.content_type === 'original' && (
        <div className="mb-4">
          <label className="block text-gray-700 font-semibold mb-2">Full Content</label>
          <RichTextEditor
            value={form.content}
            onChange={(content) => setForm({...form, content})}
          />
          <p className="text-sm text-gray-500 mt-1">Use the toolbar above to format your content</p>
        </div>
      )}
      
      <div className="mb-6">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setForm({...form, is_active: e.target.checked})}
            className="w-5 h-5"
          />
          <span className="text-gray-700 font-semibold">Active (visible on website)</span>
        </label>
      </div>
      
      <div className="flex gap-4">
        <button type="submit" className="bg-black text-white px-6 py-2 font-semibold hover:bg-black-light transition-colors">
          Save
        </button>
        <button type="button" onClick={onCancel} className="bg-gray-300 text-gray-700 px-6 py-2 font-semibold hover:bg-gray-400 transition-colors">
          Cancel
        </button>
      </div>
    </form>
  );
};

// Image Upload Component
const ImageUpload = ({ value, onChange, authConfig, label = "Image" }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(resolveImageUrl(value) || '');
  const fileInputRef = React.useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Show preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload file
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(`${API}/admin/upload`, formData, {
        ...authConfig,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      // Store just the relative path - resolveImageUrl will add the correct domain
      const uploadedUrl = res.data.url;
      onChange(uploadedUrl);
      setPreview(resolveImageUrl(uploadedUrl));
    } catch (err) {
      alert('Failed to upload image. Please try again.');
      setPreview(value || '');
    } finally {
      setUploading(false);
    }
  };

  const handleUrlChange = (e) => {
    const url = e.target.value;
    onChange(url);
    setPreview(url);
  };

  const handleRemove = () => {
    onChange('');
    setPreview('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div>
      <label className="block text-gray-700 font-semibold mb-2">{label}</label>
      
      {/* Preview */}
      {preview && (
        <div className="mb-3 relative inline-block">
          <img 
            src={preview} 
            alt="Preview" 
            className="w-24 h-24 object-contain border rounded bg-gray-50"
          />
          <button 
            type="button"
            onClick={handleRemove}
            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
          >
            ×
          </button>
        </div>
      )}
      
      <div className="flex gap-2 items-center">
        <input
          type="file"
          ref={fileInputRef}
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
          id={`file-upload-${label.replace(/\s/g, '-')}`}
        />
        <label 
          htmlFor={`file-upload-${label.replace(/\s/g, '-')}`}
          className={`cursor-pointer bg-gray-100 border border-gray-300 px-4 py-2 rounded text-sm font-semibold hover:bg-gray-200 transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
        >
          {uploading ? 'Uploading...' : 'Upload Image'}
        </label>
        <span className="text-gray-400 text-sm">or</span>
        <input
          type="text"
          placeholder="Paste image URL"
          value={preview.startsWith('data:') ? '' : (preview || '')}
          onChange={handleUrlChange}
          className="flex-1 border border-gray-300 px-3 py-2 rounded text-sm focus:outline-none focus:border-black"
        />
      </div>
    </div>
  );
};

// Gallery Form (with bulk upload support for new photos)
const GalleryForm = ({ photo, onSave, onCancel, authConfig, onBulkSave }) => {
  const [form, setForm] = useState({
    url: photo?.url || '',
    caption: photo?.caption || '',
    order: photo?.order || 0,
    is_active: photo?.is_active ?? true
  });
  
  // Bulk upload state
  const [bulkMode, setBulkMode] = useState(!photo); // Default to bulk mode for new photos
  const [bulkFiles, setBulkFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const dropZoneRef = React.useRef(null);
  const bulkInputRef = React.useRef(null);
  
  // Cropping state
  const [cropperImage, setCropperImage] = useState(null);
  const [cropperIndex, setCropperIndex] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.url) {
      alert('Please upload or enter an image URL');
      return;
    }
    onSave(form);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (files.length > 30) {
      alert('Maximum 30 photos at a time');
      return;
    }
    setBulkFiles(prev => [...prev, ...files].slice(0, 30));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleBulkFileSelect = (e) => {
    const files = Array.from(e.target.files).filter(f => f.type.startsWith('image/'));
    if (files.length > 30) {
      alert('Maximum 30 photos at a time');
      return;
    }
    setBulkFiles(prev => [...prev, ...files].slice(0, 30));
  };

  const removeBulkFile = (index) => {
    setBulkFiles(prev => prev.filter((_, i) => i !== index));
  };
  
  // Open cropper for a specific image
  const openCropper = (index) => {
    const file = bulkFiles[index];
    const reader = new FileReader();
    reader.onload = () => {
      setCropperImage(reader.result);
      setCropperIndex(index);
    };
    reader.readAsDataURL(file);
  };
  
  // Handle crop complete
  const handleCropComplete = (croppedBlob) => {
    // Create a new file from the cropped blob
    const croppedFile = new File([croppedBlob], bulkFiles[cropperIndex].name, { type: 'image/jpeg' });
    
    // Replace the file in bulkFiles
    setBulkFiles(prev => {
      const newFiles = [...prev];
      newFiles[cropperIndex] = croppedFile;
      return newFiles;
    });
    
    // Close cropper
    setCropperImage(null);
    setCropperIndex(null);
  };

  const handleBulkUpload = async () => {
    if (bulkFiles.length === 0) return;
    
    setUploading(true);
    setUploadProgress({ current: 0, total: bulkFiles.length });
    
    const uploadedPhotos = [];
    
    for (let i = 0; i < bulkFiles.length; i++) {
      const file = bulkFiles[i];
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const res = await axios.post(`${API}/admin/upload`, formData, {
          ...authConfig,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        
        // Store just the relative path
        const uploadedUrl = res.data.url;
        uploadedPhotos.push({
          url: uploadedUrl,
          caption: '',
          order: i,
          is_active: true
        });
        
        setUploadProgress({ current: i + 1, total: bulkFiles.length });
      } catch (err) {
        logError("uploadFile", err);
      }
    }
    
    setUploading(false);
    
    if (uploadedPhotos.length > 0 && onBulkSave) {
      await onBulkSave(uploadedPhotos);
    }
    
    setBulkFiles([]);
    onCancel();
  };

  // If editing existing photo, show single edit form
  if (photo) {
    return (
      <form onSubmit={handleSubmit}>
        <h3 className="font-heading text-xl text-black uppercase mb-4">Edit Photo</h3>
        
        <div className="mb-4">
          <label className="block text-gray-700 font-semibold mb-2">Current Photo</label>
          <img 
            src={resolveImageUrl(form.url)} 
            alt="Current" 
            className="w-32 h-32 object-cover rounded border"
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Caption</label>
            <input
              type="text"
              value={form.caption}
              onChange={(e) => setForm({...form, caption: e.target.value})}
              className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
              placeholder="Describe this photo..."
            />
          </div>
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Display Order</label>
            <input
              type="number"
              value={form.order}
              onChange={(e) => setForm({...form, order: parseInt(e.target.value) || 0})}
              className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
              min="0"
            />
          </div>
        </div>
        
        <div className="mb-6">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({...form, is_active: e.target.checked})}
              className="w-5 h-5"
            />
            <span className="text-gray-700 font-semibold">Active (visible on website)</span>
          </label>
        </div>
        
        <div className="flex gap-4">
          <button type="submit" className="bg-black text-white px-6 py-2 font-semibold hover:bg-black-light transition-colors">
            Save
          </button>
          <button type="button" onClick={onCancel} className="bg-gray-300 text-gray-700 px-6 py-2 font-semibold hover:bg-gray-400 transition-colors">
            Cancel
          </button>
        </div>
      </form>
    );
  }

  // Bulk upload form for new photos
  return (
    <div>
      <h3 className="font-heading text-xl text-black uppercase mb-4">Add Photos (up to 30)</h3>
      
      {/* Drop zone */}
      <div
        ref={dropZoneRef}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-black transition-colors cursor-pointer mb-4"
        onClick={() => bulkInputRef.current?.click()}
      >
        <input
          type="file"
          ref={bulkInputRef}
          accept="image/*"
          multiple
          onChange={handleBulkFileSelect}
          className="hidden"
        />
        <Camera className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600 font-semibold mb-1">Drop photos here or click to select</p>
        <p className="text-gray-400 text-sm">Upload up to 30 photos at once. Add captions later.</p>
      </div>
      
      {/* Preview of selected files */}
      {bulkFiles.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-gray-700 font-semibold">{bulkFiles.length} photo(s) selected</p>
            <p className="text-sm text-gray-500">Click <Crop size={14} className="inline" /> to crop any photo</p>
          </div>
          <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 max-h-64 overflow-y-auto p-1">
            {bulkFiles.map((file, index) => (
              <div key={`bulk-${file.name}-${file.size}`} className="relative group">
                <img 
                  src={URL.createObjectURL(file)} 
                  alt={`Preview ${index + 1}`}
                  className="w-full aspect-square object-cover rounded border-2 border-transparent group-hover:border-black transition-colors"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                  <button
                    type="button"
                    onClick={() => openCropper(index)}
                    className="bg-white text-black p-1.5 rounded hover:bg-gray-100"
                    title="Crop image"
                  >
                    <Crop size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeBulkFile(index)}
                    className="bg-red-500 text-white p-1.5 rounded hover:bg-red-600"
                    title="Remove"
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Image Cropper Modal */}
      {cropperImage && (
        <ImageCropperModal
          imageSrc={cropperImage}
          onCropComplete={handleCropComplete}
          onCancel={() => { setCropperImage(null); setCropperIndex(null); }}
          aspectRatio={1}
        />
      )}
      
      {/* Upload progress */}
      {uploading && (
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="animate-spin w-4 h-4 border-2 border-black border-t-transparent rounded-full"></div>
            <span className="text-gray-600">Uploading {uploadProgress.current} of {uploadProgress.total}...</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-black h-2 rounded-full transition-all duration-300"
              style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
            ></div>
          </div>
        </div>
      )}
      
      <div className="flex gap-4">
        <button 
          type="button"
          onClick={handleBulkUpload}
          disabled={bulkFiles.length === 0 || uploading}
          className="bg-campaign-red text-white px-6 py-2 font-semibold hover:bg-campaign-red-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {uploading ? 'Uploading...' : `Upload ${bulkFiles.length} Photo${bulkFiles.length !== 1 ? 's' : ''}`}
        </button>
        <button type="button" onClick={onCancel} className="bg-gray-300 text-gray-700 px-6 py-2 font-semibold hover:bg-gray-400 transition-colors">
          Cancel
        </button>
      </div>
    </div>
  );
};

// Endorsement Form
const EndorsementForm = ({ endorsement, onSave, onCancel, authConfig }) => {
  const [form, setForm] = useState({
    name: endorsement?.name || '',
    title: endorsement?.title || '',
    organization: endorsement?.organization || '',
    quote: endorsement?.quote || '',
    image_url: endorsement?.image_url || '',
    endorsement_type: endorsement?.endorsement_type || 'individual',
    is_active: endorsement?.is_active ?? true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <form onSubmit={handleSubmit}>
      <h3 className="font-heading text-xl text-black uppercase mb-4">{endorsement ? 'Edit Endorsement' : 'Add Endorsement'}</h3>
      
      <div className="mb-4">
        <label className="block text-gray-700 font-semibold mb-2">Endorsement Type *</label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="endorsement_type"
              value="organization"
              checked={form.endorsement_type === 'organization'}
              onChange={(e) => setForm({...form, endorsement_type: e.target.value})}
              className="w-4 h-4"
            />
            <span className="flex items-center gap-1"><Building2 size={16} /> Organization</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="endorsement_type"
              value="individual"
              checked={form.endorsement_type === 'individual'}
              onChange={(e) => setForm({...form, endorsement_type: e.target.value})}
              className="w-4 h-4"
            />
            <span className="flex items-center gap-1"><User size={16} /> Individual</span>
          </label>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-gray-700 font-semibold mb-2">
            {form.endorsement_type === 'organization' ? 'Organization Name *' : 'Name *'}
          </label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({...form, name: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
            placeholder={form.endorsement_type === 'organization' ? 'e.g., Yonkers Republican Committee' : 'e.g., Mike Spano'}
            required
          />
        </div>
        <div>
          <label className="block text-gray-700 font-semibold mb-2">
            {form.endorsement_type === 'organization' ? 'Tagline/Description' : 'Title'}
          </label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({...form, title: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
            placeholder={form.endorsement_type === 'organization' ? 'e.g., Serving Yonkers since 1920' : 'e.g., Mayor of Yonkers'}
          />
        </div>
        {form.endorsement_type === 'individual' && (
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Organization</label>
            <input
              type="text"
              value={form.organization}
              onChange={(e) => setForm({...form, organization: e.target.value})}
              className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
              placeholder="e.g., Yonkers City Council"
            />
          </div>
        )}
      </div>
      
      {/* Image Upload */}
      <div className="mb-4">
        <ImageUpload 
          value={form.image_url}
          onChange={(url) => setForm({...form, image_url: url})}
          authConfig={authConfig}
          label={form.endorsement_type === 'organization' ? 'Organization Logo' : 'Photo'}
        />
      </div>
      
      {form.endorsement_type === 'individual' && (
        <div className="mb-4">
          <label className="block text-gray-700 font-semibold mb-2">Quote</label>
          <textarea
            value={form.quote}
            onChange={(e) => setForm({...form, quote: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
            rows="3"
            placeholder="Optional endorsement quote..."
          />
        </div>
      )}
      
      <div className="mb-6">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setForm({...form, is_active: e.target.checked})}
            className="w-5 h-5"
          />
          <span className="text-gray-700 font-semibold">Active (visible on website)</span>
        </label>
      </div>
      
      <div className="flex gap-4">
        <button type="submit" className="bg-black text-white px-6 py-2 font-semibold hover:bg-black-light transition-colors">
          Save
        </button>
        <button type="button" onClick={onCancel} className="bg-gray-300 text-gray-700 px-6 py-2 font-semibold hover:bg-gray-400 transition-colors">
          Cancel
        </button>
      </div>
    </form>
  );
};

// Issue Form
const IssueForm = ({ issue, onSave, onCancel }) => {
  const [form, setForm] = useState({
    title: issue?.title || '',
    icon: issue?.icon || 'Shield',
    description: issue?.description || '',
    details: issue?.details || [''],
    order: issue?.order || 0,
    is_active: issue?.is_active ?? true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Filter out empty details
    const cleanedDetails = form.details.filter(d => d.trim() !== '');
    onSave({ ...form, details: cleanedDetails });
  };

  const addDetail = () => {
    setForm({ ...form, details: [...form.details, ''] });
  };

  const removeDetail = (index) => {
    setForm({ ...form, details: form.details.filter((_, i) => i !== index) });
  };

  const updateDetail = (index, value) => {
    const newDetails = [...form.details];
    newDetails[index] = value;
    setForm({ ...form, details: newDetails });
  };

  const iconOptions = Object.keys(ICON_MAP);

  return (
    <form onSubmit={handleSubmit}>
      <h3 className="font-heading text-xl text-black uppercase mb-4">{issue ? 'Edit Issue' : 'Add Issue'}</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="md:col-span-2">
          <label className="block text-gray-700 font-semibold mb-2">Issue Title *</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({...form, title: e.target.value})}
            required
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
            placeholder="e.g., Fix Public Safety"
          />
        </div>
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Icon</label>
          <select
            value={form.icon}
            onChange={(e) => setForm({...form, icon: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
          >
            {iconOptions.map(icon => (
              <option key={icon} value={icon}>{icon}</option>
            ))}
          </select>
        </div>
      </div>
      
      <div className="mb-4">
        <label className="block text-gray-700 font-semibold mb-2">Description *</label>
        <textarea
          value={form.description}
          onChange={(e) => setForm({...form, description: e.target.value})}
          required
          className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
          rows="2"
          placeholder="Brief description shown on the card..."
        />
      </div>
      
      <div className="mb-4">
        <label className="block text-gray-700 font-semibold mb-2">Key Points</label>
        {form.details.map((detail, index) => (
          <div key={`detail-${index}-${form.details.length}`} className="flex gap-2 mb-2">
            <input
              type="text"
              value={detail}
              onChange={(e) => updateDetail(index, e.target.value)}
              className="flex-1 border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
              placeholder={`Point ${index + 1}...`}
            />
            <button
              type="button"
              onClick={() => removeDetail(index)}
              className="px-3 py-2 bg-red-100 text-red-600 rounded hover:bg-red-200"
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addDetail}
          className="text-black text-sm font-semibold hover:underline"
        >
          + Add another point
        </button>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Display Order</label>
          <input
            type="number"
            value={form.order}
            onChange={(e) => setForm({...form, order: parseInt(e.target.value) || 0})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
            min="0"
          />
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({...form, is_active: e.target.checked})}
              className="w-5 h-5"
            />
            <span className="text-gray-700 font-semibold">Active (visible on website)</span>
          </label>
        </div>
      </div>
      
      <div className="flex gap-4">
        <button type="submit" className="bg-black text-white px-6 py-2 font-semibold hover:bg-black-light transition-colors">
          Save
        </button>
        <button type="button" onClick={onCancel} className="bg-gray-300 text-gray-700 px-6 py-2 font-semibold hover:bg-gray-400 transition-colors">
          Cancel
        </button>
      </div>
    </form>
  );
};

// Admin Page Wrapper
const AdminPage = () => {
  const [auth, setAuth] = useState(null);

  const handleLogin = (credentials) => {
    setAuth(credentials);
    // Use sessionStorage instead of localStorage for security
    // Session expires when browser tab is closed
    sessionStorage.setItem('adminAuth', JSON.stringify(credentials));
  };

  const handleLogout = () => {
    setAuth(null);
    sessionStorage.removeItem('adminAuth');
  };

  useEffect(() => {
    const savedAuth = sessionStorage.getItem('adminAuth');
    if (savedAuth) {
      try {
        setAuth(JSON.parse(savedAuth));
      } catch (err) {
        sessionStorage.removeItem('adminAuth');
      }
    }
  }, []);

  if (!auth) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return <AdminDashboard auth={auth} onLogout={handleLogout} />;
};

// Signup Page
const SignupPage = () => {
  usePageTitle("Get Involved");
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    zip: '',
    wants_email: true,
    wants_text: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email) return;
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/subscribe`, formData);
      setSubmitStatus('success');
    } catch (err) {
      setSubmitStatus('error');
    }
    setIsSubmitting(false);
  };

  return (
    <div className="pt-20" data-testid="signup-page">
      {/* Hero */}
      <section className="py-20 bg-[#000033]">
        <div className="container-campaign text-center">
          <h1 className="font-heading text-5xl md:text-7xl text-white uppercase mb-4">Get Involved</h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Join Anthony Merante's campaign to fight for taxpayers in Albany.
          </p>
        </div>
      </section>
      
      {/* Signup Form */}
      <section className="py-20">
        <div className="container-campaign">
          <div className="max-w-2xl mx-auto">
            {submitStatus === 'success' ? (
              <div className="bg-green-50 border border-green-200 p-12 rounded text-center">
                <CheckCircle className="w-20 h-20 text-green-600 mx-auto mb-6" />
                <h2 className="font-heading text-3xl text-[#000033] uppercase mb-4">Thank You!</h2>
                <p className="text-lg text-gray-700 mb-6">
                  You've joined the movement. We'll keep you updated on campaign news and events.
                </p>
                <Link 
                  to="/"
                  className="inline-block bg-[#3366cc] text-white px-8 py-3 font-heading uppercase tracking-wide hover:bg-[#2855b3] transition-colors"
                >
                  Back to Home
                </Link>
              </div>
            ) : (
              <>
                <h2 className="font-heading text-3xl text-[#000033] uppercase mb-2 text-center">
                  Sign Up for Updates
                </h2>
                <p className="text-gray-600 text-center mb-8">
                  Receive campaign updates, event invitations, and ways to support Anthony.
                </p>
                
                <form onSubmit={handleSubmit} className="bg-white p-8 rounded shadow-lg" data-testid="signup-page-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-[#000033] font-semibold mb-2">First Name</label>
                      <input
                        type="text"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-[#3366cc]"
                        placeholder="First Name"
                      />
                    </div>
                    <div>
                      <label className="block text-[#000033] font-semibold mb-2">Last Name</label>
                      <input
                        type="text"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-[#3366cc]"
                        placeholder="Last Name"
                      />
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-[#000033] font-semibold mb-2">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-[#3366cc]"
                      placeholder="your@email.com"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-[#000033] font-semibold mb-2">Phone (optional)</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-[#3366cc]"
                        placeholder="(555) 555-5555"
                      />
                    </div>
                    <div>
                      <label className="block text-[#000033] font-semibold mb-2">ZIP Code (optional)</label>
                      <input
                        type="text"
                        name="zip"
                        value={formData.zip}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-[#3366cc]"
                        placeholder="10701"
                      />
                    </div>
                  </div>
                  
                  <div className="mb-6 space-y-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="wants_email"
                        checked={formData.wants_email}
                        onChange={handleChange}
                        className="w-5 h-5 text-[#3366cc]"
                      />
                      <span className="text-gray-700">I'd like to receive email updates</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="wants_text"
                        checked={formData.wants_text}
                        onChange={handleChange}
                        className="w-5 h-5 text-[#3366cc]"
                      />
                      <span className="text-gray-700">I'd like to receive text message updates</span>
                    </label>
                  </div>
                  
                  {submitStatus === 'error' && (
                    <p className="text-red-600 mb-4">Something went wrong. Please try again.</p>
                  )}
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#3366cc] hover:bg-[#2855b3] text-white font-heading uppercase tracking-wider py-4 text-lg transition-colors disabled:opacity-50"
                  >
                    {isSubmitting ? 'Signing Up...' : 'Join the Campaign'}
                  </button>
                </form>
                
                {/* Donate CTA */}
                <div className="mt-8 text-center p-6 bg-gray-50 rounded">
                  <p className="text-gray-700 mb-4">Ready to help even more?</p>
                  <a 
                    href={CAMPAIGN_DATA.donateUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-[#c41920] text-white px-8 py-3 font-heading uppercase tracking-wide hover:bg-[#a31518] transition-colors"
                  >
                    Donate Now
                  </a>
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

// Contact Page - Dedicated page with contact form and info
const ContactPage = () => {
  usePageTitle("Contact Us");
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) return;
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', message: '' });
    } catch (err) {
      setSubmitStatus('error');
    }
    setIsSubmitting(false);
  };

  return (
    <div className="pt-20" data-testid="contact-page">
      {/* Hero */}
      <section className="py-20 bg-[#000033]">
        <div className="container-campaign text-center">
          <h1 className="font-heading text-5xl md:text-7xl text-white uppercase mb-4">Contact Us</h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Have a question or want to get in touch? We'd love to hear from you.
          </p>
        </div>
      </section>
      
      {/* Contact Content */}
      <section className="py-20">
        <div className="container-campaign">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="font-heading text-3xl text-[#000033] uppercase mb-6">Get In Touch</h2>
              <p className="text-gray-600 mb-8 text-lg leading-relaxed">
                Whether you have questions about Anthony's campaign, want to volunteer, or just want to share your thoughts, we're here to listen.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#3366cc] rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg text-[#000033] uppercase mb-1">Phone</h3>
                    <a href={`tel:${CAMPAIGN_DATA.contact.phone}`} className="text-[#3366cc] hover:underline text-lg" data-testid="contact-phone">
                      {CAMPAIGN_DATA.contact.phone}
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#3366cc] rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg text-[#000033] uppercase mb-1">Email</h3>
                    <a href={`mailto:${CAMPAIGN_DATA.contact.email}`} className="text-[#3366cc] hover:underline text-lg" data-testid="contact-email">
                      {CAMPAIGN_DATA.contact.email}
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#3366cc] rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPinned className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg text-[#000033] uppercase mb-1">Campaign Office</h3>
                    <p className="text-gray-700 text-lg" data-testid="contact-address">
                      46 Davenport Road<br />
                      Yonkers, NY 10710
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Social Links */}
              <div className="mt-10">
                <h3 className="font-heading text-lg text-[#000033] uppercase mb-4">Follow Us</h3>
                <div className="flex space-x-4">
                  {CAMPAIGN_DATA.facebook && (
                    <a 
                      href={CAMPAIGN_DATA.facebook} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="w-12 h-12 bg-[#000033] hover:bg-[#3366cc] rounded-full flex items-center justify-center transition-colors"
                      data-testid="contact-facebook"
                    >
                      <Facebook className="w-5 h-5 text-white" />
                    </a>
                  )}
                  {CAMPAIGN_DATA.instagram && (
                    <a 
                      href={CAMPAIGN_DATA.instagram} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="w-12 h-12 bg-[#000033] hover:bg-[#3366cc] rounded-full flex items-center justify-center transition-colors"
                      data-testid="contact-instagram"
                    >
                      <Instagram className="w-5 h-5 text-white" />
                    </a>
                  )}
                </div>
              </div>
            </div>
            
            {/* Contact Form */}
            <div>
              {submitStatus === 'success' ? (
                <div className="bg-green-50 border border-green-200 p-12 rounded text-center">
                  <CheckCircle className="w-20 h-20 text-green-600 mx-auto mb-6" />
                  <h2 className="font-heading text-3xl text-[#000033] uppercase mb-4">Message Sent!</h2>
                  <p className="text-lg text-gray-700 mb-6">
                    Thank you for reaching out. We'll get back to you as soon as possible.
                  </p>
                  <button 
                    onClick={() => setSubmitStatus(null)}
                    className="inline-block bg-[#3366cc] text-white px-8 py-3 font-heading uppercase tracking-wide hover:bg-[#2855b3] transition-colors"
                    data-testid="send-another-message-btn"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <div className="bg-gray-50 p-8 rounded-lg">
                  <h2 className="font-heading text-2xl text-[#000033] uppercase mb-6">Send a Message</h2>
                  
                  <form onSubmit={handleSubmit} data-testid="contact-form">
                    <div className="mb-4">
                      <label className="block text-[#000033] font-semibold mb-2">Your Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-[#3366cc] focus:ring-2 focus:ring-[#3366cc]/20"
                        placeholder="John Doe"
                        data-testid="contact-name-input"
                      />
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-[#000033] font-semibold mb-2">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-[#3366cc] focus:ring-2 focus:ring-[#3366cc]/20"
                        placeholder="john@example.com"
                        data-testid="contact-email-input"
                      />
                    </div>
                    
                    <div className="mb-6">
                      <label className="block text-[#000033] font-semibold mb-2">Message *</label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-[#3366cc] focus:ring-2 focus:ring-[#3366cc]/20 resize-none"
                        placeholder="How can we help you?"
                        data-testid="contact-message-input"
                      />
                    </div>
                    
                    {submitStatus === 'error' && (
                      <p className="text-red-600 mb-4" data-testid="contact-error">
                        Something went wrong. Please try again.
                      </p>
                    )}
                    
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#3366cc] hover:bg-[#2855b3] text-white font-heading uppercase tracking-wider py-4 text-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="contact-submit-btn"
                    >
                      {isSubmitting ? (
                        'Sending...'
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Send Message
                        </>
                      )}
                    </button>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Main App Layout
const MainLayout = ({ children }) => {
  return (
    <>
      <Navbar />
      <main>{children}</main>
      <Footer />
    </>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<MainLayout><Home /></MainLayout>} />
          <Route path="/about" element={<MainLayout><AboutPage /></MainLayout>} />
          <Route path="/events" element={<MainLayout><EventsPage /></MainLayout>} />
          <Route path="/news" element={<MainLayout><NewsPage /></MainLayout>} />
          <Route path="/contact" element={<MainLayout><ContactPage /></MainLayout>} />
          <Route path="/signup" element={<MainLayout><SignupPage /></MainLayout>} />
          <Route path="/admin" element={<AdminPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
